import { useEffect, useRef, useState } from 'react';
import LanguageSelector from './LanguageSelector';
import LabelDesigner, { cfgAusSettings, settingsAusCfg } from './LabelDesigner';
import SubShelfEditor from './SubShelfEditor';
import BatchTutorialVideo from './BatchTutorialVideo';
import { parseSub, formatSub, zaehleNachnamen } from '../services/regalUnterfach';
import { useTranslation } from '../hooks/useTranslation';
import { makeTr, trDyn } from '../services/trLookup';
import { usePremium } from '../contexts/PremiumContext';
import { getCustomFeatures } from '../services/customFeatures';
import {
  isWiesnAccount,
  setBevollmaechtigterEnabled,
  setDhlStickEnabled,
  getDhlStickIp,
  setDhlStickIp,
} from '../services/dhlUsbStick';
import { collectLocalSettings, getCachedProfile, saveSetup, setCachedShareCode, PROFILE_FEATURES } from '../services/accountProfile';
import { isPrinterSupported, getPrinterConfig, guessPrinterLang, parseLabelRand, setLabelRand, setPrinterLang, setLabelSize, parseCarrierSort, formatCarrierSort } from '../services/printerService';
import { createProfileCode, deleteProfileCode, fetchSharedProfile } from '../services/serverDataService';
import AddressBookModal from './AddressBookModal';
import DevicesModal from './DevicesModal';
import ShelfCodesModal from './ShelfCodesModal';
import { getLocalAddressBook, downloadExampleCsv, getPrivacyConsentText } from '../services/addressBookService';
import {
  dpdStatus, dpdRequestCode, dpdVerifyCode, dpdLogout,
  setDpdOn, setDpdAnnahmeOn, setDpdAusgabeOn, vorladenStart as dpdVorladenStart,
} from '../services/dpd';
import {
  vintedStatus, vintedLogin, vintedLogout,
  setAnzeigeOn as setVintedOn, setAnnahmeOn as setVintedAnnahmeOn, setAusgabeOn as setVintedAusgabeOn,
  vorladenStart as vintedVorladenStart,
} from '../services/vinted';

/**
 * 🧭 Setup — führt Schritt für Schritt durch alle Einstellungen und speichert sie als Konto-Profil
 * (gilt für alle Geräte des Kontos; ein neues Gerät übernimmt sie beim Anmelden).
 *
 * Vorbelegung: vorhandenes Profil, sonst die Einstellungen dieses Geräts (= bisheriges Verhalten).
 * Nur wenn das Konto noch kein Profil hat und Niederländisch gewählt wird, lädt Schritt 1 die
 * Vorlage für niederländische Abholpunkte — jeder Schritt bleibt änderbar.
 * Gespeichert wird erst im letzten Schritt (die Sprache wechselt sofort, wie bisher der Sprach-Knopf).
 */

// Heutiger Standard der App (wenn am Gerät nichts eingestellt ist)
const STANDARD = {
  kiosk_label_lang: 'cpcl', kiosk_label_layout: 'hausnummer', kiosk_label_size: '100x150', kiosk_label_rahmen: '0',
  kiosk_label_autoprint: '1', kiosk_regal_auto_letter: '0', kiosk_inapp_camera: '0', kiosk_series_mode: '0',
  kiosk_qr_regal_scan: '0', kiosk_label_kopf: 'hausnummer', kiosk_label_zeilen: 'fach,name,datum', kiosk_label_dienst: '0',
  kiosk_regal_sub: 'keine', kiosk_capture_sender: '0', kiosk_list_carrier: '0', kiosk_vinted_sort: '0', kiosk_carrier_sort: 'keine',
  kiosk_label_code: 'keine', kiosk_label_gruss: '', kiosk_label_grafik: 'keine',
  kiosk_branche: 'paketshop', kiosk_addressbook_enabled: '0', kiosk_list_notify_mode: 'staff',
  kiosk_auto_notify: '0', kiosk_require_signature: '0', kiosk_tablet_mode: '0',
  kiosk_auto_number: '0', kiosk_id_first: '0', kiosk_double_label: '0',
  kiosk_privacy_consent: '0', kiosk_privacy_text: '', kiosk_delete_days: '5', kiosk_ignore_recipient: '',
  kiosk_capture_palette: '0', kiosk_bevollmaechtigter: '0', kiosk_dhl_stick_on: '0', kiosk_dhl_stick_ip: '192.168.137.51',
  kiosk_shop_country: 'de',
  kiosk_vinted_bridge_on: '0', kiosk_vinted_live_annahme: '0', kiosk_vinted_live_ausgabe: '0',
  kiosk_dpd_enabled: '0', kiosk_dpd_live_annahme: '0', kiosk_dpd_live_ausgabe: '0',
};

// Etikett-Einstellungen, die am Drucker des einzelnen Geräts hängen (nicht im Konto-Profil)
const GERAETE_SCHLUESSEL = ['kiosk_label_lang', 'kiosk_label_size', 'kiosk_label_rand'];
const GERAETE_STANDARD = { kiosk_label_lang: 'cpcl', kiosk_label_size: '100x150', kiosk_label_rand: '0,0,0' };

// „ABCD-EFGH" — so wird ein Profil-Code angezeigt (Eingabe: Groß/klein und Trennzeichen egal)
const codeAnzeige = (c) => (c && c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : (c || ''));

// Vorlage „Abholpunkt Niederlande" (Einrichtung von Green Goat, Rotterdam)
const VORLAGE_NL = {
  settings: {
    kiosk_branche: 'pakketpunt_nl',
    kiosk_shop_country: 'nl',
    kiosk_addressbook_enabled: '0',
    kiosk_list_notify_mode: 'staff',
    kiosk_label_lang: 'zpl', kiosk_label_layout: 'nachname', kiosk_label_size: '100x150', kiosk_label_rahmen: '1',
    kiosk_label_autoprint: '1', kiosk_regal_auto_letter: '1', kiosk_inapp_camera: '1', kiosk_series_mode: '1',
  },
  features: { nachnameSortierung: true, vintedImport: true, staffMode: true, batchBarcodes: true, seriesMode: true },
  carriers: ['postnl', 'dhl', 'dhlexpress', 'dpd', 'gls', 'ups', 'vinted', 'mondialrelay'],
};

const CARRIERS = [
  ['dhl', 'DHL'], ['dhlexpress', 'DHL Express'], ['hermes', 'Hermes'], ['dpd', 'DPD'], ['gls', 'GLS'], ['ups', 'UPS'], ['postnl', 'PostNL'],
  ['amazon', 'Amazon'], ['fedex', 'FedEx'], ['vinted', 'Vinted Go'], ['mondialrelay', 'Mondial Relay'], ['inpost', 'InPost'], ['grenzpaket', 'Grenzpaket'],
];

const STEPS = ['language', 'carriers', 'sorting', 'printer', 'capture', 'handout', 'staff', 'done'];

const DELETE_DAYS_OPTIONS = [
  ['0', { key: 'd01', de: 'Sofort (1 Std.)', en: 'Immediately (1h)', nl: 'Direct (1 uur)' }],
  ['1', { key: 'd02', de: '1 Tag', en: '1 day', nl: '1 dag' }],
  ['3', { key: 'd03', de: '3 Tage', en: '3 days', nl: '3 dagen' }],
  ['5', { key: 'd04', de: '5 Tage (Standard)', en: '5 days (Default)', nl: '5 dagen (Standaard)' }],
  ['14', { key: 'd05', de: '14 Tage', en: '14 days', nl: '14 dagen' }],
  ['30', { key: 'd06', de: '30 Tage', en: '30 days', nl: '30 dagen' }],
  ['90', { de: '90 Tage', en: '90 days', nl: '90 dagen' }],
  ['365', { de: '1 Jahr (365 T.)', en: '1 year (365d)', nl: '1 jaar (365 d.)' }],
  ['1825', { de: '5 Jahre (Max.)', en: '5 years (Max)', nl: '5 jaar (Max.)' }],
];

function deriveShopCountry(localSet, profSettings, aktiv, lang) {
  const explicit = profSettings?.kiosk_shop_country || localSet?.kiosk_shop_country;
  if (explicit) return explicit;
  const br = profSettings?.kiosk_branche || localSet?.kiosk_branche;
  if (aktiv?.grenzpaket || br === 'grenzpaket') return 'ch';
  if (br === 'pakketpunt_nl' || lang === 'nl') return 'nl';
  if (lang === 'fr') return 'fr';
  return 'de';
}

export default function SetupWizard({ onClose, colors, onOpenPrinter, printerOpen, pakete, darkMode, onDarkMode, initialStep = 0 }) {
  const { t, language } = useTranslation();
  const { isPremium } = usePremium();
  const dark = colors?.darkMode;
  const tr = makeTr(language);

  // Ausgangsstand: Profil des Kontos, sonst dieses Gerät
  const start = useRef(null);
  if (!start.current) {
    const profil = getCachedProfile();
    const aktiv = getCustomFeatures();
    const features = {};
    for (const n of PROFILE_FEATURES) features[n] = profil ? !!profil.features?.[n] : !!aktiv[n];
    try { if (localStorage.getItem('kiosk_batch_barcodes_disabled') === '1') features.batchBarcodes = false; } catch { /* */ }
    const localSet = collectLocalSettings();
    const settings = { ...STANDARD, ...localSet, ...(profil?.settings || {}) };
    settings.kiosk_shop_country = deriveShopCountry(localSet, profil?.settings, aktiv, language);
    // Falls kiosk_addressbook_enabled noch nie explizit gesetzt wurde, aus Branche ableiten (Paketshop = 0, Büro/Wohnanlage/Click&Collect = 1)
    if (localSet.kiosk_addressbook_enabled === undefined && profil?.settings?.kiosk_addressbook_enabled === undefined) {
      const b = settings.kiosk_branche || 'paketshop';
      settings.kiosk_addressbook_enabled = (b === 'buero' || b === 'wohnanlage' || b === 'click_collect') ? '1' : '0';
    }
    if (localSet.kiosk_list_notify_mode === undefined && profil?.settings?.kiosk_list_notify_mode === undefined) {
      settings.kiosk_list_notify_mode = settings.kiosk_auto_notify === '1'
        ? 'auto'
        : ((settings.kiosk_branche === 'paketshop' || settings.kiosk_branche === 'pakketpunt_nl') ? 'staff' : 'ask');
    }
    // Serienmodus ist standardmäßig aus (nur an, wenn ausdrücklich eingeschaltet)
    if (!profil) settings.kiosk_series_mode = features.seriesMode && localStorage.getItem('kiosk_series_mode') === '1' ? '1' : '0';
    // „frisch" = an diesem Gerät wurde noch nichts eingestellt und keine Funktion ist aktiv
    const frisch = !profil && !PROFILE_FEATURES.some((n) => aktiv[n])
      && Object.keys(localSet).every((k) => k === 'app_language');
    // Kein Drucker gekoppelt und nie ein Druckertyp gewählt → Schritt „Etikettendrucker" startet auf „Kein Etikettendrucker"
    let ohneDrucker = false;
    try { ohneDrucker = !getPrinterConfig()?.address && localStorage.getItem('kiosk_label_lang') === null && !profil?.settings?.kiosk_label_lang && !aktiv.grenzpaket; } catch { /* */ }
    // 🖨️ Druckertyp, Etikettgröße und Ränder kommen primär aus dem Konto-Profil (sofern vorhanden),
    // sonst aus den lokalen Geräteeinstellungen
    const druckerTipp = guessPrinterLang(getPrinterConfig()?.name);
    for (const k of GERAETE_SCHLUESSEL) {
      let v = profil?.settings?.[k] || null;
      if (!v) { try { v = localStorage.getItem(k); } catch { /* */ } }
      settings[k] = (k === 'kiosk_label_lang' && druckerTipp) ? druckerTipp : (v || (k === 'kiosk_label_lang' && aktiv.grenzpaket ? 'zpl' : GERAETE_STANDARD[k]));
    }
    // 🇨🇭 Grenzpaket-Konten erhalten automatisch das Original-Grenzpaket-Quittungslayout als Vorbelegung
    if (aktiv.grenzpaket && !localSet.kiosk_label_layout && !profil?.settings?.kiosk_label_layout) {
      settings.kiosk_label_layout = 'grenzpaket';
      if (!localSet.kiosk_qr_regal_scan && !profil?.settings?.kiosk_qr_regal_scan && !localSet.kiosk_regal_auto_letter && !profil?.settings?.kiosk_regal_auto_letter) {
        settings.kiosk_qr_regal_scan = '1';
        settings.kiosk_regal_auto_letter = '0';
        features.nachnameSortierung = true;
      }
    }
    if (aktiv.palettenvertrieb && !localSet.kiosk_capture_palette && !profil?.settings?.kiosk_capture_palette) {
      settings.kiosk_capture_palette = '1';
      features.palettenvertrieb = true;
    }
    const initialCarriers = Array.isArray(profil?.carriers) && profil.carriers.length > 0
      ? profil.carriers
      : (aktiv.grenzpaket ? ['grenzpaket'] : []);
    start.current = { hatProfil: !!profil, frisch, settings, features, carriers: initialCarriers, ohneDrucker,
      shareCode: (profil && typeof profil.shareCode === 'string') ? profil.shareCode : '' };
  }

  const [step, setStep] = useState(() => (Number.isInteger(initialStep) && initialStep >= 0 && initialStep < STEPS.length ? initialStep : 0));
  // Paketliste dieses Kontos → wie voll würden die Unterfächer? (einmal beim Öffnen gezählt)
  const nachnamen = useRef(null);
  if (!nachnamen.current) nachnamen.current = zaehleNachnamen((pakete || []).filter((p) => p && !p.deleted && !/^vinted\b/i.test(String(p.name || ''))));
  const [settings, setSettings] = useState(start.current.settings);
  const [features, setFeatures] = useState(start.current.features);
  const [carriers, setCarriers] = useState(start.current.carriers);
  const [vorlage, setVorlage] = useState(null); // 'nl' = Vorlage geladen
  const [ohneDrucker, setOhneDrucker] = useState(start.current.ohneDrucker); // Schritt „Etikettendrucker" übersprungen
  const [busy, setBusy] = useState(false);
  const [hilfeOffen, setHilfeOffen] = useState(null); // welche Erklärung („?") ist aufgeklappt
  useEffect(() => { setHilfeOffen(null); }, [step]); // neuer Schritt → Erklärungen zu
  const [error, setError] = useState('');
  const [showAddressBookModal, setShowAddressBookModal] = useState(false);
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [showShelfCodesModal, setShowShelfCodesModal] = useState(false);
  const [addressBookCount, setAddressBookCount] = useState(() => getLocalAddressBook().length);

  // 🔌 Paketdienst-Konten im Setup (DPD Pickup & Vinted Go — nur in verfügbaren Ländern wie NL / BE / FR)
  const [dpdAuth, setDpdAuth] = useState(null);
  const [dpdPudoId, setDpdPudoId] = useState('');
  const [dpdOtpCode, setDpdOtpCode] = useState('');
  const [dpdCodeSent, setDpdCodeSent] = useState(false);
  const [dpdBusy, setDpdBusy] = useState(false);
  const [dpdMsg, setDpdMsg] = useState(null);

  const [vintedAuth, setVintedAuth] = useState(null);
  const [vintedEmail, setVintedEmail] = useState('');
  const [vintedPasswort, setVintedPasswort] = useState('');
  const [vintedBusy, setVintedBusy] = useState(false);
  const [vintedMsg, setVintedMsg] = useState(null);

  const enableClientCustomFeature = (featKey) => {
    try {
      const raw = JSON.parse(localStorage.getItem('kiosk_custom_features') || '{}') || {};
      const next = { ...raw, [featKey]: true };
      localStorage.setItem('kiosk_custom_features', JSON.stringify(next));
      window.dispatchEvent(new CustomEvent('kiosk:custom-features', { detail: next }));
    } catch { /* */ }
  };

  useEffect(() => {
    if (STEPS[step] !== 'carriers') return;
    const country = settings.kiosk_shop_country || 'de';
    const isPickup = ['nl', 'be', 'fr'].includes(country);
    const cf = getCustomFeatures();
    if (isPickup || cf.dpd || settings.kiosk_dpd_enabled === '1') {
      dpdStatus().then((s) => {
        if (s && typeof s === 'object') {
          if (s.ok === false && s.reason === 'offline') {
            setDpdAuth((prev) => prev?.angemeldet ? prev : { offline: true });
          } else {
            setDpdAuth(s);
            if (s.pudoId && !dpdPudoId) setDpdPudoId(s.pudoId);
          }
        }
      }).catch(() => {});
    }
    if (isPickup || cf.vinted || settings.kiosk_vinted_bridge_on === '1') {
      vintedStatus().then((s) => {
        if (s && typeof s === 'object') {
          if (s.ok === false && s.reason === 'offline') {
            setVintedAuth((prev) => prev?.angemeldet ? prev : { offline: true });
          } else {
            setVintedAuth(s);
          }
        }
      }).catch(() => {});
    }
  }, [step, settings.kiosk_shop_country, settings.kiosk_branche, settings.kiosk_dpd_enabled, settings.kiosk_vinted_bridge_on, language]); // eslint-disable-line

  const handleDpdRequestCode = async () => {
    const cleanId = dpdPudoId.trim().toUpperCase();
    if (!cleanId) return;
    setDpdBusy(true);
    setDpdMsg(null);
    try {
      const r = await dpdRequestCode(cleanId);
      if (r && r.ok) {
        setDpdCodeSent(true);
        const mailHint = r.mail ? ` (${r.mail})` : '';
        setDpdMsg({
          ok: true,
          text: tr(
            `6-stelliger Sicherheitscode wurde per E-Mail gesendet${mailHint}.`,
            `6-digit security code sent by email${mailHint}.`,
            `6-cijferige beveiligingscode per e-mail verzonden${mailHint}.`
          ),
        });
      } else {
        setDpdMsg({ ok: false, text: (r && r.message) || tr('Fehler beim Anfordern des Codes', 'Failed to request code', 'Fout bij aanvragen van code') });
      }
    } finally {
      setDpdBusy(false);
    }
  };

  const handleDpdVerifyCode = async () => {
    const cleanId = dpdPudoId.trim().toUpperCase();
    const cleanCode = dpdOtpCode.trim();
    if (!cleanId || !cleanCode) return;
    setDpdBusy(true);
    setDpdMsg(null);
    try {
      const r = await dpdVerifyCode(cleanId, cleanCode);
      if (r && r.ok) {
        setDpdOtpCode('');
        setDpdCodeSent(false);
        enableClientCustomFeature('dpd');
        setDpdOn(true);
        setSettings((s) => ({ ...s, kiosk_dpd_enabled: '1' }));
        setDpdMsg({
          ok: true,
          text: tr(
            '✅ Erfolgreich mit DPD Pickup verbunden! Gilt automatisch für alle Geräte deines Kontos.',
            '✅ Successfully connected to DPD Pickup! Automatically shared across all devices on your account.',
            '✅ Succesvol verbonden met DPD Pickup! Geldt automatisch voor alle apparaten van je account.'
          ),
        });
        const s = await dpdStatus();
        if (s) setDpdAuth(s);
        dpdVorladenStart(true).catch(() => {});
      } else {
        setDpdMsg({ ok: false, text: (r && r.message) || tr('Code ungültig oder abgelaufen', 'Invalid or expired code', 'Code ongeldig of verlopen') });
      }
    } finally {
      setDpdBusy(false);
    }
  };

  const handleDpdDisconnect = async () => {
    const ok = window.confirm(
      tr(
        'DPD-Pickup-Verbindung für dieses Konto wirklich trennen? Alle verbundenen Geräte werden getrennt.',
        'Really disconnect DPD Pickup for this account? All connected devices will be disconnected.',
        'DPD Pickup-koppeling voor dit account echt verbreken? Alle gekoppelde apparaten worden ontkoppeld.'
      )
    );
    if (!ok) return;
    setDpdBusy(true);
    try {
      await dpdLogout();
      setDpdOn(false);
      setDpdAnnahmeOn(false);
      setDpdAusgabeOn(false);
      setSettings((s) => ({
        ...s,
        kiosk_dpd_enabled: '0',
        kiosk_dpd_live_annahme: '0',
        kiosk_dpd_live_ausgabe: '0',
      }));
      const s = await dpdStatus();
      setDpdAuth(s);
      setDpdMsg(null);
    } finally {
      setDpdBusy(false);
    }
  };

  const handleVintedLogin = async () => {
    if (!vintedEmail.trim() || !vintedPasswort) return;
    setVintedBusy(true);
    setVintedMsg(null);
    try {
      const r = await vintedLogin(vintedEmail.trim(), vintedPasswort);
      setVintedPasswort('');
      if (r && r.ok) {
        enableClientCustomFeature('vinted');
        setVintedOn(true);
        setSettings((s) => ({ ...s, kiosk_vinted_bridge_on: '1' }));
        setVintedMsg({
          ok: true,
          text: tr(
            '✅ Erfolgreich mit Vinted Go verbunden! Gilt automatisch für alle Geräte deines Kontos.',
            '✅ Successfully connected to Vinted Go! Automatically shared across all devices on your account.',
            '✅ Succesvol verbonden met Vinted Go! Geldt automatisch voor alle apparaten van je account.'
          ),
        });
        const s = await vintedStatus();
        if (s) setVintedAuth(s);
        vintedVorladenStart().catch(() => {});
      } else {
        setVintedMsg({ ok: false, text: (r && r.message) || tr('Anmeldung fehlgeschlagen', 'Login failed', 'Aanmelden mislukt') });
      }
    } finally {
      setVintedBusy(false);
    }
  };

  const handleVintedDisconnect = async () => {
    const ok = window.confirm(
      tr(
        'Vinted-Go-Verbindung für dieses Konto wirklich trennen? Alle verbundenen Geräte werden getrennt.',
        'Really disconnect Vinted Go for this account? All connected devices will be disconnected.',
        'Vinted Go-koppeling voor dit account echt verbreken? Alle gekoppelde apparaten worden ontkoppeld.'
      )
    );
    if (!ok) return;
    setVintedBusy(true);
    try {
      await vintedLogout();
      setVintedOn(false);
      setVintedAnnahmeOn(false);
      setVintedAusgabeOn(false);
      setSettings((s) => ({
        ...s,
        kiosk_vinted_bridge_on: '0',
        kiosk_vinted_live_annahme: '0',
        kiosk_vinted_live_ausgabe: '0',
      }));
      const s = await vintedStatus();
      setVintedAuth(s);
      setVintedMsg(null);
    } finally {
      setVintedBusy(false);
    }
  };
  useEffect(() => {
    const updateCount = () => setAddressBookCount(getLocalAddressBook().length);
    window.addEventListener('kiosk:addressbook-changed', updateCount);
    return () => window.removeEventListener('kiosk:addressbook-changed', updateCount);
  }, []);

  // 🔄 Falls auf einem frischen Gerät das Server-Profil (/premium/status) erst kurz nach dem Öffnen
  // des Setups eintrifft, übernimmt das Setup die Konto-Einstellungen sofort (solange noch auf Schritt 1).
  useEffect(() => {
    const onProfileArrived = () => {
      const profil = getCachedProfile();
      if (!profil || start.current?.hatProfil) return;
      const aktiv = getCustomFeatures();
      const nextFeatures = {};
      for (const n of PROFILE_FEATURES) nextFeatures[n] = !!(profil.features?.[n] || aktiv[n]);
      const localSet = collectLocalSettings();
      const nextSettings = { ...STANDARD, ...localSet, ...(profil.settings || {}) };
      nextSettings.kiosk_shop_country = deriveShopCountry(localSet, profil.settings, aktiv, nextSettings.app_language || language);
      start.current = {
        ...start.current,
        hatProfil: true,
        frisch: false,
        settings: nextSettings,
        features: nextFeatures,
        carriers: profil.carriers || [],
        ohneDrucker: false,
        shareCode: typeof profil.shareCode === 'string' ? profil.shareCode : '',
      };
      setSettings(nextSettings);
      setFeatures(nextFeatures);
      setCarriers(profil.carriers || []);
      setOhneDrucker(false);
      if (typeof profil.shareCode === 'string') setShareCode(profil.shareCode);
    };
    window.addEventListener('kiosk:profile-applied', onProfileArrived);
    return () => window.removeEventListener('kiosk:profile-applied', onProfileArrived);
  }, []);
  // 🔑 Profil per Code: weitergeben (letzter Schritt) und laden (erster Schritt)
  const [shareCode, setShareCode] = useState(start.current.shareCode);
  const [shareStatus, setShareStatus] = useState(''); // '' | 'busy' | 'kopiert' | 'fehler'
  const [codeOffen, setCodeOffen] = useState(false);
  const [codeEingabe, setCodeEingabe] = useState('');
  const [codeStatus, setCodeStatus] = useState(''); // '' | 'laden' | 'ok' | 'fehlt' | 'fehler'

  const set = (key, value) => setSettings((s) => ({ ...s, [key]: value }));
  const feat = (name, on) => setFeatures((f) => ({ ...f, [name]: !!on }));

  // Profil eines anderen Kontos als Vorlage laden (Kopie; gespeichert wird wie immer erst am Ende).
  // Die Sprache und die Ränder dieses Geräts bleiben, wie sie sind.
  const ladeCode = async () => {
    setCodeStatus('laden');
    try {
      const res = await fetchSharedProfile(codeEingabe);
      const p = res && res.profile;
      if (!p) { setCodeStatus('fehlt'); return; }
      setSettings((s) => ({
        ...STANDARD,
        ...(p.settings || {}),
        app_language: language,
        kiosk_shop_country: p.settings?.kiosk_shop_country || s.kiosk_shop_country || 'de',
        kiosk_label_lang: s.kiosk_label_lang,
        kiosk_label_size: s.kiosk_label_size,
        kiosk_label_rand: s.kiosk_label_rand,
      }));
      const f = {};
      for (const n of PROFILE_FEATURES) f[n] = !!(p.features && p.features[n]);
      setFeatures(f);
      setCarriers(Array.isArray(p.carriers) ? p.carriers : []);
      setVorlage('code');
      setCodeStatus('ok');
    } catch (e) {
      setCodeStatus(e && (e.status === 404 || e.status === 400) ? 'fehlt' : 'fehler');
    }
  };
  const teilen = async () => {
    setShareStatus('busy');
    try { const r = await createProfileCode(); const c = (r && r.code) || ''; setShareCode(c); setCachedShareCode(c); setShareStatus(''); } catch { setShareStatus('fehler'); }
  };
  const teilenBeenden = async () => {
    setShareStatus('busy');
    try { await deleteProfileCode(); setShareCode(''); setCachedShareCode(''); setShareStatus(''); } catch { setShareStatus('fehler'); }
  };
  const codeKopieren = async () => {
    try { await navigator.clipboard.writeText(codeAnzeige(shareCode)); setShareStatus('kopiert'); } catch { /* Anzeige genügt, Code lässt sich abtippen */ }
  };

  // Schritt 1: Sprache gewählt → merken; Niederländisch lädt (nur ohne vorhandenes Profil) die NL-Vorlage
  const ersterLauf = useRef(true);
  useEffect(() => {
    setSettings((s) => ({ ...s, app_language: language }));
    const erster = ersterLauf.current;
    ersterLauf.current = false;
    if (start.current.hatProfil) return;
    if (vorlage === 'code') return; // per Code geladenes Profil bleibt, egal welche Sprache gewählt wird
    // Beim Öffnen (Sprache schon Niederländisch, nichts gewählt): Vorlage nur auf einem frischen
    // Gerät laden — ein bereits eingerichtetes Gerät behält seine Einstellungen als Vorbelegung.
    if (erster && !start.current.frisch) return;
    if (language === 'nl' && vorlage !== 'nl') {
      setSettings((s) => ({ ...s, ...VORLAGE_NL.settings, app_language: 'nl' }));
      setFeatures({ ...VORLAGE_NL.features });
      setCarriers([...VORLAGE_NL.carriers]);
      setVorlage('nl');
      setOhneDrucker(false); // die Vorlage bringt einen Druckertyp mit
    } else if (language !== 'nl' && vorlage === 'nl') {
      setSettings({ ...start.current.settings, app_language: language });
      setFeatures({ ...start.current.features });
      setCarriers([...start.current.carriers]);
      setVorlage(null);
      setOhneDrucker(start.current.ohneDrucker);
    }
  }, [language]); // eslint-disable-line

  // Drucker-Dialog (Kopplung) ändert Typ/Größe direkt am Gerät → nach dem Schließen übernehmen
  const druckerWarOffen = useRef(false);
  useEffect(() => {
    if (printerOpen) { druckerWarOffen.current = true; return; }
    if (!druckerWarOffen.current) return;
    druckerWarOffen.current = false;
    const lokal = collectLocalSettings();
    setSettings((s) => {
      const n = { ...s };
      for (const k of ['kiosk_label_lang', 'kiosk_label_size', 'kiosk_label_rand', 'kiosk_label_layout',
        'kiosk_label_rahmen', 'kiosk_label_autoprint', 'kiosk_label_kopf', 'kiosk_label_zeilen',
        'kiosk_label_dienst', 'kiosk_label_code', 'kiosk_label_gruss', 'kiosk_label_grafik']) {
        if (lokal[k] !== undefined) n[k] = lokal[k];
      }
      return n;
    });
  }, [printerOpen]);

  const toggleCarrier = (id) => {
    const an = !carriers.includes(id);
    setCarriers((c) => (an ? [...c, id] : c.filter((x) => x !== id)));
    if (id === 'vinted') {
      feat('vintedImport', an); // Vinted-Pakete tragen keinen Namen → Namens-Import
      if (!an) {
        setSettings((s) => ({
          ...s,
          kiosk_vinted_bridge_on: '0',
          kiosk_vinted_live_annahme: '0',
          kiosk_vinted_live_ausgabe: '0',
        }));
      }
    }
    if (id === 'dpd' && !an) {
      setSettings((s) => ({
        ...s,
        kiosk_dpd_enabled: '0',
        kiosk_dpd_live_annahme: '0',
        kiosk_dpd_live_ausgabe: '0',
      }));
    }
  };

  const [showAllCarriersSort, setShowAllCarriersSort] = useState(false);
  const carrierSortMap = parseCarrierSort(settings.kiosk_carrier_sort);
  if (!carrierSortMap.vinted && settings.kiosk_vinted_sort === '1') {
    carrierSortMap.vinted = 'kurznummer';
  }
  const setCarrierSort = (carrierId, mode) => {
    setSettings((s) => {
      const map = parseCarrierSort(s.kiosk_carrier_sort);
      if (!map.vinted && s.kiosk_vinted_sort === '1') map.vinted = 'kurznummer';
      if (!mode || mode === 'standard') delete map[carrierId];
      else map[carrierId] = mode;
      const nextVintedSort = map.vinted === 'kurznummer' ? '1' : (carrierId === 'vinted' ? '0' : s.kiosk_vinted_sort);
      return {
        ...s,
        kiosk_carrier_sort: formatCarrierSort(map),
        kiosk_vinted_sort: nextVintedSort,
      };
    });
    if (mode === 'nachname') feat('nachnameSortierung', true);
  };

  const sortBarcodeOnly = settings.kiosk_qr_regal_scan === '1' && settings.kiosk_regal_auto_letter !== '1';
  const sortNachname = !sortBarcodeOnly && !!features.nachnameSortierung
    && (settings.kiosk_label_layout === 'nachname' || settings.kiosk_regal_auto_letter === '1');
  const sortHausnummer = !sortNachname && !sortBarcodeOnly;
  const setSortierung = (modus) => {
    const hatNachnameOverride = Object.values(carrierSortMap).includes('nachname');
    if (modus === 'barcode') {
      feat('nachnameSortierung', true);
      setSettings((s) => ({
        ...s,
        kiosk_regal_auto_letter: '0',
        kiosk_qr_regal_scan: '1',
        kiosk_label_layout: (s.kiosk_label_layout === 'eigen' || s.kiosk_label_layout === 'grenzpaket') ? s.kiosk_label_layout : 'nachname',
      }));
      return;
    }
    const nachname = !!modus;
    feat('nachnameSortierung', nachname || hatNachnameOverride);
    // Etikett-Vorlage nur mitziehen, solange kein eigenes Layout gewählt ist
    setSettings((s) => ({ ...s, kiosk_regal_auto_letter: nachname ? '1' : '0',
      kiosk_qr_regal_scan: (!nachname && s.kiosk_qr_regal_scan === '1' && s.kiosk_regal_auto_letter !== '1') ? '0' : s.kiosk_qr_regal_scan,
      kiosk_label_layout: (s.kiosk_label_layout === 'eigen' || s.kiosk_label_layout === 'grenzpaket') ? s.kiosk_label_layout : (nachname ? 'nachname' : 'hausnummer') }));
  };
  const setSerie = (an) => {
    feat('seriesMode', an);
    setSettings((s) => ({ ...s, kiosk_series_mode: an ? '1' : '0', kiosk_inapp_camera: an ? '1' : s.kiosk_inapp_camera }));
  };

  // 🧭 „Was bist du?" — wählt die passende Vorbelegung für den Einsatzbereich (alle Schalter bleiben danach einzeln änderbar)
  const waehleBranche = (b) => {
    const aktiv = getCustomFeatures();
    if (b === 'paketshop') {
      setSettings((s) => ({
        ...s,
        kiosk_branche: 'paketshop',
        kiosk_shop_country: s.kiosk_shop_country || 'de',
        kiosk_addressbook_enabled: '0',
        kiosk_list_notify_mode: 'staff',
        kiosk_label_layout: (s.kiosk_label_layout === 'eigen' || s.kiosk_label_layout === 'grenzpaket') ? s.kiosk_label_layout : 'hausnummer',
        kiosk_regal_auto_letter: '0',
        kiosk_auto_notify: '0',
        // Bereits gesetzte Unterschriften-Pflicht oder lange Löschfrist (z. B. Grenzpaket) beim Antippen von „Paketshop" nicht zurücksetzen!
        kiosk_require_signature: s.kiosk_require_signature === '1' ? '1' : '0',
        kiosk_tablet_mode: '0',
        kiosk_auto_number: '0',
        kiosk_id_first: '0',
        kiosk_double_label: '0',
        kiosk_privacy_consent: '0',
        kiosk_delete_days: (s.kiosk_delete_days && s.kiosk_delete_days !== '14' && s.kiosk_delete_days !== '1') ? s.kiosk_delete_days : '5',
      }));
      setFeatures((f) => ({ ...f, nachnameSortierung: !!aktiv.grenzpaket }));
      if (!carriers.length) setCarriers(aktiv.grenzpaket ? ['grenzpaket'] : ['dhl', 'hermes', 'dpd', 'gls', 'ups']);
      setVorlage('paketshop');
    } else if (b === 'pakketpunt_nl') {
      const druckerTipp = guessPrinterLang(getPrinterConfig()?.name);
      let hatLokalenDrucker = false;
      try { hatLokalenDrucker = !!localStorage.getItem('kiosk_label_lang'); } catch { /* */ }
      setSettings((s) => ({
        ...s,
        ...VORLAGE_NL.settings,
        kiosk_label_lang: druckerTipp || (hatLokalenDrucker ? s.kiosk_label_lang : VORLAGE_NL.settings.kiosk_label_lang),
        kiosk_branche: 'pakketpunt_nl',
        kiosk_shop_country: (s.kiosk_shop_country && s.kiosk_shop_country !== 'de') ? s.kiosk_shop_country : 'nl',
        kiosk_addressbook_enabled: '0',
        kiosk_list_notify_mode: 'staff',
        kiosk_auto_notify: '0',
        kiosk_require_signature: '0',
        kiosk_auto_number: '0',
        kiosk_id_first: '0',
        kiosk_double_label: '0',
        kiosk_privacy_consent: '0',
        kiosk_delete_days: '5',
      }));
      setFeatures({ ...VORLAGE_NL.features });
      setCarriers([...VORLAGE_NL.carriers]);
      setOhneDrucker(false);
      setVorlage('nl');
    } else if (b === 'buero') {
      setSettings((s) => ({
        ...s,
        kiosk_branche: 'buero',
        kiosk_addressbook_enabled: '1',
        kiosk_list_notify_mode: 'auto',
        kiosk_label_layout: s.kiosk_label_layout === 'eigen' ? 'eigen' : 'nachname',
        kiosk_regal_auto_letter: '1',
        kiosk_capture_sender: '1',
        kiosk_auto_notify: '1',
        kiosk_require_signature: '1',
        kiosk_tablet_mode: '0',
        kiosk_auto_number: '0',
        kiosk_id_first: '0',
        kiosk_double_label: '0',
        kiosk_privacy_consent: '0',
        kiosk_delete_days: '14',
      }));
      setFeatures((f) => ({ ...f, nachnameSortierung: true }));
      if (!carriers.length) setCarriers(['dhl', 'dhlexpress', 'ups', 'fedex', 'dpd', 'gls', 'amazon']);
      setVorlage('buero');
    } else if (b === 'wohnanlage') {
      setSettings((s) => ({
        ...s,
        kiosk_branche: 'wohnanlage',
        kiosk_addressbook_enabled: '1',
        kiosk_list_notify_mode: 'auto',
        kiosk_label_layout: s.kiosk_label_layout === 'eigen' ? 'eigen' : 'nachname',
        kiosk_regal_auto_letter: '1',
        kiosk_auto_notify: '1',
        kiosk_tablet_mode: '1',
        kiosk_require_signature: '0',
        kiosk_auto_number: '0',
        kiosk_id_first: '0',
        kiosk_double_label: '0',
        kiosk_privacy_consent: '0',
        kiosk_delete_days: '14',
      }));
      setFeatures((f) => ({ ...f, nachnameSortierung: true }));
      if (!carriers.length) setCarriers(['dhl', 'hermes', 'amazon', 'dpd', 'gls', 'ups']);
      setVorlage('wohnanlage');
    } else if (b === 'gepaeck') {
      setSettings((s) => ({
        ...s,
        kiosk_branche: 'gepaeck',
        kiosk_addressbook_enabled: '0',
        kiosk_list_notify_mode: 'staff',
        kiosk_inapp_camera: '1',
        kiosk_auto_number: '1',
        kiosk_id_first: '0',
        kiosk_double_label: '1',
        kiosk_privacy_consent: '1',
        kiosk_require_signature: '0',
        kiosk_auto_notify: '0',
        kiosk_delete_days: '1',
      }));
      setCarriers([]);
      setVorlage('gepaeck');
    } else if (b === 'click_collect') {
      setSettings((s) => ({
        ...s,
        kiosk_branche: 'click_collect',
        kiosk_addressbook_enabled: '1',
        kiosk_list_notify_mode: 'auto',
        kiosk_label_layout: s.kiosk_label_layout === 'eigen' ? 'eigen' : 'nachname',
        kiosk_regal_auto_letter: '1',
        kiosk_auto_notify: '1',
        kiosk_require_signature: '1',
        kiosk_tablet_mode: '0',
        kiosk_auto_number: '1',
        kiosk_id_first: '0',
        kiosk_double_label: '1',
        kiosk_privacy_consent: '0',
        kiosk_delete_days: '14',
      }));
      setFeatures((f) => ({ ...f, nachnameSortierung: true }));
      setCarriers([]);
      setVorlage('click_collect');
    }
  };

  const speichern = async () => {
    setBusy(true); setError('');
    try {
      const zuSpeichern = { ...settings, kiosk_dark_mode: dark ? '1' : '0' };
      if (ohneDrucker) {
        const alt = getCachedProfile()?.settings || {};
        for (const k of GERAETE_SCHLUESSEL) {
          if (alt[k] !== undefined) zuSpeichern[k] = alt[k];
          else delete zuSpeichern[k];
        }
      }
      await saveSetup({ settings: zuSpeichern, features, carriers });
      setDpdOn(zuSpeichern.kiosk_dpd_enabled === '1');
      setDpdAnnahmeOn(zuSpeichern.kiosk_dpd_live_annahme === '1');
      setDpdAusgabeOn(zuSpeichern.kiosk_dpd_live_ausgabe === '1');
      setVintedOn(zuSpeichern.kiosk_vinted_bridge_on === '1');
      setVintedAnnahmeOn(zuSpeichern.kiosk_vinted_live_annahme === '1');
      setVintedAusgabeOn(zuSpeichern.kiosk_vinted_live_ausgabe === '1');
      if (zuSpeichern.kiosk_dpd_enabled === '1') enableClientCustomFeature('dpd');
      if (zuSpeichern.kiosk_vinted_bridge_on === '1') enableClientCustomFeature('vinted');
      if (!ohneDrucker) {
        let gespeicherterTyp = settings.kiosk_label_lang;
        try { gespeicherterTyp = localStorage.getItem('kiosk_label_lang') || settings.kiosk_label_lang; } catch { /* */ }
        setPrinterLang(gespeicherterTyp);
        setLabelSize(settings.kiosk_label_size);
        setLabelRand(parseLabelRand(settings.kiosk_label_rand));
      }
      try { window.dispatchEvent(new CustomEvent('kiosk:premium-refresh')); } catch { /* */ } // Funktionen neu laden
      onClose(true);
    } catch (e) {
      setError(t('setup.saveFailed'));
    }
    setBusy(false);
  };

  // ── Bausteine ──
  const farbe = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', flaeche: dark ? '#1b1f27' : '#f6f7f9' };
  const karte = (aktiv) => ({
    display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', marginTop: 8, borderRadius: 10, cursor: 'pointer',
    border: `2px solid ${aktiv ? '#1976d2' : farbe.rand}`, background: aktiv ? (dark ? '#1c2b3f' : '#e8f1fc') : 'transparent',
    color: farbe.text, fontSize: 15, boxSizing: 'border-box',
  });
  // Jede Option: Karte zum Antippen + „?"-Knopf, der eine Erklärung mit Beispiel aufklappt
  // (Text unter setup.help.<id>; fehlt er, gibt es keinen Knopf).
  const Karte = ({ id, aktiv, onClick, zeichen, titel, text, stern }) => {
    const schluessel = `setup.help.${id}`;
    const hilfe = t(schluessel);
    const hatHilfe = hilfe && hilfe !== schluessel;
    const offen = hilfeOffen === id;
    return (
      <div style={{ position: 'relative' }}>
        <button type="button" data-testid={`setup-${id}`} aria-pressed={aktiv} onClick={onClick} style={{ ...karte(aktiv), paddingRight: hatHilfe ? 50 : 14 }}>
          <strong>{zeichen} {titel}{stern ? ' ⭐' : ''}</strong>
          {text && <div style={{ fontSize: 13, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>{text}</div>}
        </button>
        {hatHilfe && (
          <button type="button" data-testid={`setup-help-${id}`} aria-label={t('setup.helpLabel')} aria-expanded={offen}
            onClick={() => setHilfeOffen(offen ? null : id)}
            style={{ position: 'absolute', top: 17, right: 9, width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 16, fontWeight: 700,
              border: `1px solid ${offen ? '#1976d2' : farbe.rand}`, background: offen ? '#1976d2' : (dark ? '#23272f' : '#fff'), color: offen ? '#fff' : farbe.sub }}>?</button>
        )}
        {offen && (
          <div data-testid={`setup-helptext-${id}`} style={{ margin: '4px 0 2px', padding: '10px 12px', borderRadius: 8, background: farbe.flaeche, fontSize: 13, lineHeight: 1.55, color: farbe.text }}>
            💡 {hilfe}
          </div>
        )}
      </div>
    );
  };
  const Wahl = ({ id, aktiv, onClick, titel, text, stern }) => (
    <Karte id={id} aktiv={aktiv} onClick={onClick} zeichen={aktiv ? '◉' : '○'} titel={titel} text={text} stern={stern} />
  );
  const Schalter = ({ id, an, onChange, titel, text, stern }) => (
    <Karte id={id} aktiv={an} onClick={() => onChange(!an)} zeichen={an ? '☑' : '☐'} titel={titel} text={text} stern={stern} />
  );
  const btn = (primary) => ({
    flex: 1, padding: '12px', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer',
    border: primary ? 'none' : `1px solid ${farbe.rand}`, background: primary ? '#1976d2' : 'transparent',
    color: primary ? '#fff' : farbe.text, opacity: busy ? 0.6 : 1,
  });

  const name = STEPS[step];
  const premiumNoetig = PROFILE_FEATURES.some((n) => features[n]);
  const zpl = settings.kiosk_label_lang === 'zpl';
  const carrierNamen = CARRIERS.filter(([id]) => carriers.includes(id)).map(([, n]) => n).join(', ');
  const branche = settings.kiosk_branche || 'paketshop';
  const branchenName = {
    paketshop: tr('📦 Paketshop / Kiosk', '📦 Parcel Shop / Kiosk', '📦 Pakketshop / Kiosk'),
    pakketpunt_nl: tr('🇳🇱 Pakketpunt (NL / International)', '🇳🇱 Parcel Point (NL / Int.)', '🇳🇱 Pakketpunt (NL / Internationaal)'),
    buero: tr('🏢 Büro / Empfang / Co-Working', '🏢 Office / Reception / Mailroom', '🏢 Kantoor / Receptie / Co-working'),
    wohnanlage: tr('🏠 Wohnanlage / Wohnheim / Hotel', '🏠 Residential / Dorm / Hotel', '🏠 Wooncomplex / Hotel / Camping'),
    gepaeck: tr('🧥 Gepäck / Garderobe / Fundbüro / Werkstatt', '🧥 Luggage / Cloakroom / Lost & Found', '🧥 Bagage / Garderobe / Gevonden voorwerpen'),
    click_collect: tr('🛍️ Click & Collect / Abholtheke / Reparatur', '🛍️ Click & Collect / Pickup Counter / Repair', '🛍️ Click & Collect / Afhaalbalie / Reparatie'),
  }[branche] || '📦 Paketshop';

  const stepTitle = name === 'handout'
    ? tr('Benachrichtigung, Ausgabe & Datenschutz', 'Notifications, Handout & Privacy', 'Meldingen, Afgifte & Privacy')
    : t(`setup.${name}.title`);
  const stepIntro = name === 'handout'
    ? tr(
        'Lege fest, ob Empfänger automatisch per E-Mail benachrichtigt werden, bei der Abholung unterschreiben oder Datenschutzbedingungen bestätigen.',
        'Configure automatic email notifications, pickup signatures, tablet search mode, and retention periods.',
        'Stel automatische e-mailmeldingen, handtekening bij afhalen en bewaartermijnen in.'
      )
    : t(`setup.${name}.intro`);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2900,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div data-testid="setup-wizard" style={{ background: dark ? '#23272f' : '#fff', color: farbe.text, borderRadius: 14,
        width: '100%', maxWidth: 460, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <div style={{ padding: '16px 18px 10px', borderBottom: `1px solid ${farbe.rand}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: 17 }}>🧭 {t('setup.title')}</strong>
            <button type="button" data-testid="setup-close" aria-label={t('common.cancel')} onClick={() => onClose(false)}
              style={{ background: 'transparent', border: 'none', fontSize: 26, lineHeight: 1, cursor: 'pointer', color: farbe.text }}>×</button>
          </div>
          <div data-testid="setup-step" style={{ fontSize: 12, color: farbe.sub, marginTop: 2 }}>
            {t('setup.stepOf', { n: step + 1, total: STEPS.length })} · {stepTitle}
          </div>
        </div>

        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
          <div style={{ fontSize: 14, color: farbe.sub, lineHeight: 1.5 }}>{stepIntro}</div>

          {name === 'language' && (
            <>
              {/* 🔑 Profil-Code: Einstellungen eines anderen Kontos als Vorlage laden */}
              <div style={{ marginTop: 10 }}>
                <button type="button" data-testid="setup-code-toggle" onClick={() => setCodeOffen(!codeOffen)}
                  style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  {codeOffen ? '▾' : '▸'} 🔑 {t('setup.share.enter')}
                </button>
                {codeOffen && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input data-testid="setup-code-input" type="text" value={codeEingabe} maxLength={12} autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                      onChange={(e) => { setCodeEingabe(e.target.value.toUpperCase()); setCodeStatus(''); }}
                      placeholder={t('setup.share.placeholder')}
                      style={{ flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 8, border: `1px solid ${farbe.rand}`, fontSize: 16, letterSpacing: 1,
                        background: dark ? '#1b1f27' : '#fff', color: farbe.text, boxSizing: 'border-box' }} />
                    <button type="button" data-testid="setup-code-load" disabled={codeStatus === 'laden' || codeEingabe.replace(/[^A-Za-z0-9]/g, '').length < 8}
                      onClick={ladeCode} style={{ ...btn(true), flex: 'none', padding: '10px 16px',
                        opacity: (codeStatus === 'laden' || codeEingabe.replace(/[^A-Za-z0-9]/g, '').length < 8) ? 0.5 : 1 }}>
                      {t('setup.share.load')}
                    </button>
                  </div>
                )}
                {codeStatus === 'ok' && (
                  <div data-testid="setup-code-ok" style={{ marginTop: 8, padding: 10, borderRadius: 8, background: farbe.flaeche, fontSize: 13, lineHeight: 1.45 }}>
                    ✅ {t('setup.share.loaded')}
                  </div>
                )}
                {(codeStatus === 'fehlt' || codeStatus === 'fehler') && (
                  <div data-testid="setup-code-error" style={{ marginTop: 8, color: '#e5484d', fontSize: 13, lineHeight: 1.45 }}>
                    ⚠️ {t(codeStatus === 'fehlt' ? 'setup.share.notFound' : 'setup.share.failed')}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 12 }}><LanguageSelector variant="list" dark={dark} /></div>
              {/* 📍 Standort / Land des Shops: steuert länderspezifische Paketdienst-Anbindungen im nächsten Schritt */}
              <div data-testid="setup-country-box" style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${farbe.rand}` }}>
                <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  📍 {tr('Standort / Land deines Shops', 'Shop Location / Country', 'Locatie / Land van je pakketpunt')}
                </strong>
                <div style={{ fontSize: 12, color: farbe.sub, lineHeight: 1.4, marginBottom: 8 }}>
                  {tr(
                    'Steuert, welche länderspezifischen Paketdienst-Logins (z. B. DPD Pickup & Vinted Go für NL/BE/FR) bei den Paketdiensten direkt angeboten werden.',
                    'Controls which country-specific carrier logins (e.g. DPD Pickup & Vinted Go for NL/BE/FR) are offered under Parcel Carriers.',
                    'Bepaalt welke landspecifieke pakketdienst-koppelingen (bijv. DPD Pickup & Vinted Go voor NL/BE/FR) bij de pakketdiensten direct worden getoond.'
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[
                    ['de', '🇩🇪 Deutschland'],
                    ['nl', '🇳🇱 Nederland'],
                    ['be', '🇧🇪 België / Belgique'],
                    ['fr', '🇫🇷 France'],
                    ['at', '🇦🇹 Österreich'],
                    ['ch', '🇨🇭 Schweiz'],
                    ['other', tr('🌍 Anderes Land', '🌍 Other country', '🌍 Ander land')],
                  ].map(([cCode, cLabel]) => {
                    const currentCountry = settings.kiosk_shop_country || 'de';
                    const active = currentCountry === cCode;
                    return (
                      <button
                        key={cCode}
                        type="button"
                        data-testid={`setup-country-${cCode}`}
                        aria-pressed={active}
                        onClick={() => {
                          set('kiosk_shop_country', cCode);
                          if ((cCode === 'nl' || cCode === 'be' || cCode === 'fr') && start.current.frisch && vorlage !== 'code') {
                            setCarriers((prev) => {
                              const next = [...prev];
                              for (const id of ['dpd', 'vinted', 'dhl', 'postnl']) {
                                if (!next.includes(id)) next.push(id);
                              }
                              return next;
                            });
                          }
                        }}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 18,
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 700,
                          border: `2px solid ${active ? '#1976d2' : farbe.rand}`,
                          background: active ? '#1976d2' : 'transparent',
                          color: active ? '#fff' : farbe.text,
                        }}
                      >
                        {cLabel}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* 🌙 Dunkelmodus: Geräte-Einstellung, wirkt sofort (Wunsch 08.10.2026: gehört ins Setup) */}
              {typeof onDarkMode === 'function' && (
                <div style={{ marginTop: 12 }}>
                  <Schalter id="setup-darkmode" an={!!darkMode} onChange={(an) => onDarkMode(an)}
                    titel={t('setup.language.darkMode')} text={t('setup.language.darkModeHint')} />
                </div>
              )}

              {/* 🧭 WAS BIST DU? — Einsatzbereich wählen & passende Felder automatisch voraktivieren */}
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${farbe.rand}` }}>
                <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  🎯 {tr('Was bist du? (Einsatzbereich wählen)', 'What is your use case?', 'Waarvoor gebruik je ParcelPilot?')}
                </strong>
                <div style={{ fontSize: 12, color: farbe.sub, lineHeight: 1.4, marginBottom: 4 }}>
                  {tr(
                    'Aktiviert automatisch die passenden Funktionen für deinen Bereich. Du kannst in den nächsten Schritten alles feinjustieren.',
                    'Automatically enables the relevant features for your area. You can customize everything in the next steps.',
                    'Schakelt automatisch de juiste functies in. Je kunt alles in de volgende stappen aanpassen.'
                  )}
                </div>
                <Wahl
                  id="branche-paketshop"
                  aktiv={branche === 'paketshop'}
                  onClick={() => waehleBranche('paketshop')}
                  titel={tr('📦 Paketshop / Kiosk (DE)', '📦 Parcel Shop / Kiosk (DE)', '📦 Pakketshop / Kiosk (DE)')}
                  text={tr('Sortierung nach Hausnummer, PLZ-Kreis-Prüfung, Paketdienste & 5 Tage Löschfrist.', 'Sort by house number, postal code check, carriers & 5-day retention.', 'Sorteren op huisnummer, postcodecontrole & pakketdiensten.')}
                />
                <Wahl
                  id="branche-pakketpunt"
                  aktiv={branche === 'pakketpunt_nl'}
                  onClick={() => waehleBranche('pakketpunt_nl')}
                  titel={tr('🇳🇱 Pakketpunt / Abholpunkt (NL / International)', '🇳🇱 Parcel Point (NL / International)', '🇳🇱 Pakketpunt / Afhaalpunt (NL / Int.)')}
                  text={tr('Sortierung nach Nachname (A–Z), Serienmodus, Vinted-Unterstützung.', 'Sort by surname (A–Z), series mode, Vinted support.', 'Sorteren op achternaam (A–Z), seriemodus, Vinted-ondersteuning.')}
                />
                <Wahl
                  id="branche-buero"
                  aktiv={branche === 'buero'}
                  onClick={() => waehleBranche('buero')}
                  titel={tr('🏢 Büro / Empfang / Co-Working (Interne Poststelle)', '🏢 Office / Reception / Co-Working Mailroom', '🏢 Kantoor / Receptie / Co-working')}
                  text={tr('Mitarbeiter-Adressbuch (CSV-Import für Microsoft/Google), Auto-E-Mail beim Scannen & Unterschrift bei Ausgabe.', 'Staff address book (Microsoft/Google CSV import), auto-email on scan & signature on pickup.', 'Medewerkers-adresboek (CSV-import), automatische e-mail & handtekening bij afhalen.')}
                />
                <Wahl
                  id="branche-wohnanlage"
                  aktiv={branche === 'wohnanlage'}
                  onClick={() => waehleBranche('wohnanlage')}
                  titel={tr('🏠 Wohnanlage / Studentenwohnheim / Hotel / Camping', '🏠 Residential / Student Dorm / Hotel / Resort', '🏠 Wooncomplex / Studentenflat / Hotel / Camping')}
                  text={tr('Bewohner-Adressbuch mit Auto-E-Mail-Benachrichtigung & Tablet-Suchmodus für Abholer.', 'Resident address book with auto-email notification & tablet search terminal.', 'Bewoners-adresboek met automatische e-mail & tablet-zoekmodus.')}
                />
                <Wahl
                  id="branche-gepaeck"
                  aktiv={branche === 'gepaeck'}
                  onClick={() => waehleBranche('gepaeck')}
                  titel={tr('🧥 Gepäckannahme / Garderobe / Fundbüro / Werkstatt', '🧥 Luggage Storage / Cloakroom / Lost & Found / Repair', '🧥 Bagageopslag / Garderobe / Gevonden voorwerpen')}
                  text={tr('Für Gegenstände ohne Namen: Auto-Nummerierung (#001) oder Ausweis zuerst, 2. Kunden-Abholbon, Datenschutz-Einwilligung & kurze Speicherfrist.', 'For items without names: auto-numbering (#001) or ID card first, 2nd customer receipt label & custom privacy consent.', 'Voor spullen zonder naam: automatische nummering (#001) of ID eerst, 2e afhaalbon & privacy-toestemming.')}
                />
                <Wahl
                  id="branche-click-collect"
                  aktiv={branche === 'click_collect'}
                  onClick={() => waehleBranche('click_collect')}
                  titel={tr('🛍️ Click & Collect / Abholtheke / Reparatur & Reinigung', '🛍️ Click & Collect / Pickup Counter / Repair & Dry Cleaning', '🛍️ Click & Collect / Afhaalbalie / Reparatie & Stomerij')}
                  text={tr('Für Laden-Bestellungen, Apotheken, Werkstätten & Textilreinigung: Kunden-Adressbuch, Auto-E-Mail mit Abhol-PIN, Laufnummer (#001), 2. Abholbon & Unterschrift.', 'For store orders, pharmacies, repair shops & dry cleaners: customer address book, auto-email with pickup PIN, auto-numbering (#001), 2nd receipt & signature.', 'Voor winkelbestellingen, apotheken, reparatie & stomerij: klanten-adresboek, auto-e-mail met afhaal-PIN, volgnummer (#001), 2e afhaalbon & handtekening.')}
                />
              </div>

              {vorlage === 'nl' && (
                <div data-testid="setup-preset-nl" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: farbe.flaeche, fontSize: 13, lineHeight: 1.45 }}>
                  ✅ {t('setup.language.presetNl')}
                </div>
              )}
            </>
          )}

          {name === 'carriers' && (() => {
            const shopCountry = settings.kiosk_shop_country || 'de';
            const isPickupCountry = ['nl', 'be', 'fr'].includes(shopCountry);
            const cf = getCustomFeatures();
            const showDpdBox = carriers.includes('dpd') && (isPickupCountry || cf.dpd || dpdAuth?.angemeldet || settings.kiosk_dpd_enabled === '1');
            const showVintedLoginBox = carriers.includes('vinted') && (isPickupCountry || cf.vinted || vintedAuth?.angemeldet || settings.kiosk_vinted_bridge_on === '1');
            const dpdPlaceholder = shopCountry === 'be' ? 'BE12345' : shopCountry === 'fr' ? 'FR12345' : 'NL24808';
            const confirmLiveBooking = (dienstName) => window.confirm(
              tr(
                `⚡ Live-Buchung für ${dienstName} einschalten?\n\nHinweis: Das ist kein Probelauf – gescannte bzw. ausgegebene Pakete werden direkt im System von ${dienstName} für deinen Shop gebucht (auf allen Geräten deines Kontos).`,
                `⚡ Enable live booking for ${dienstName}?\n\nNote: This is not a test mode – scanned or handed-out parcels will be booked directly in ${dienstName} for your shop (across all devices on your account).`,
                `⚡ Live-boeking voor ${dienstName} inschakelen?\n\nLet op: dit is geen testmodus – gescande of uitgegeven pakketten worden direct in het systeem van ${dienstName} geboekt (op alle apparaten van je account).`
              )
            );
            const inputStyle = {
              width: '100%', padding: '9px 11px', borderRadius: 8, border: `1px solid ${farbe.rand}`,
              fontSize: 14, background: dark ? '#14171d' : '#fff', color: farbe.text, boxSizing: 'border-box',
            };
            return (
              <>
                {(branche === 'gepaeck' || branche === 'click_collect') && (
                  <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: farbe.flaeche, fontSize: 13, lineHeight: 1.45 }}>
                    ℹ️ {tr(
                      'Für diesen Einsatzbereich werden meist keine Paketdienste benötigt — du kannst direkt auf „Weiter" tippen.',
                      'For this use case, parcel carriers are usually not needed — you can tap "Next".',
                      'Voor dit gebruiksdoel zijn meestal geen pakketdiensten nodig — je kunt direct op "Volgende" tikken.'
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  {CARRIERS.map(([id, label]) => {
                    const an = carriers.includes(id);
                    const hasDirectIntegration = isPickupCountry && (id === 'dpd' || id === 'vinted');
                    return (
                      <button key={id} type="button" data-testid={`setup-carrier-${id}`} aria-pressed={an} onClick={() => toggleCarrier(id)}
                        style={{ padding: '9px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 14, fontWeight: 600,
                          border: `2px solid ${an ? '#1976d2' : farbe.rand}`, background: an ? '#1976d2' : 'transparent', color: an ? '#fff' : farbe.text }}>
                        {label}{hasDirectIntegration ? ' ⚡' : ''}
                      </button>
                    );
                  })}
                </div>

                {/* 🔴 DPD Pickup Konto-Zugang & Einstellungen (nur in verfügbaren Ländern wie NL / BE / FR) */}
                {showDpdBox && (
                  <div
                    data-testid="setup-dpd-account-box"
                    style={{
                      marginTop: 14, padding: 12, borderRadius: 10,
                      border: `1.5px solid ${dpdAuth?.angemeldet ? '#22c55e' : '#ef4444'}`,
                      background: dark ? '#1b1f27' : '#fff5f5',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 14 }}>
                        🔴 {tr('DPD Pickup – Eigener Shop-Zugang & Einstellungen', 'DPD Pickup – Your Shop Login & Settings', 'DPD Pickup – Eigen shop-account & instellingen')}
                      </strong>
                      <span style={{
                        fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                        background: dpdAuth?.angemeldet ? (dark ? '#14532d' : '#dcfce7') : (dark ? '#422006' : '#fef3c7'),
                        color: dpdAuth?.angemeldet ? (dark ? '#86efac' : '#166534') : (dark ? '#fde047' : '#92400e'),
                      }}>
                        {dpdAuth?.angemeldet
                          ? `🟢 ${tr('Verbunden', 'Connected', 'Verbonden')}${dpdAuth.pudoId ? ` (${dpdAuth.pudoId})` : ''}`
                          : dpdAuth?.offline
                            ? `📡 ${tr('Offline', 'Offline', 'Offline')}`
                            : `🟡 ${tr('Noch nicht verbunden', 'Not connected yet', 'Nog niet verbonden')}`}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>
                      🔗 {tr(
                        'Unabhängige Komfort-Anbindung über deinen eigenen Pickup-Zugang (holt Empfängernamen vorab & bucht optional Annahme/Ausgabe). Alle Geräte deines PaketPilot-Kontos teilen sich diesen Zugang automatisch!',
                        'Independent convenience link using your own Pickup account (fetches recipient names in advance & optionally books intake/pickup). All devices on your ParcelPilot account automatically share this login!',
                        'Onafhankelijke koppeling via je eigen Pickup-account (haalt ontvangersnamen vooraf op & boekt optioneel inname/uitgifte). Alle apparaten van je account delen deze inlog automatisch!'
                      )}
                    </div>

                    {dpdAuth?.angemeldet ? (
                      <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: dark ? '#14171d' : '#fff', border: `1px solid ${farbe.rand}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                        <span>
                          ✅ <strong>{dpdAuth.pudoId}</strong>{dpdAuth.shopName ? ` · ${dpdAuth.shopName}` : ''}{(dpdAuth.emailHint || dpdAuth.emailHinweis) ? ` (${dpdAuth.emailHint || dpdAuth.emailHinweis})` : ''}
                        </span>
                        <button
                          type="button"
                          data-testid="setup-dpd-disconnect"
                          disabled={dpdBusy}
                          onClick={handleDpdDisconnect}
                          style={{ padding: '4px 9px', borderRadius: 6, border: `1px solid ${farbe.rand}`, background: 'transparent', color: farbe.sub, fontSize: 11.5, cursor: 'pointer' }}
                        >
                          {tr('Trennen', 'Disconnect', 'Ontkoppelen')}
                        </button>
                      </div>
                    ) : (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <input
                            type="text"
                            data-testid="setup-dpd-pudoid"
                            value={dpdPudoId}
                            onChange={(e) => setDpdPudoId(e.target.value.toUpperCase())}
                            placeholder={tr(`Pickup Shop-ID (z. B. ${dpdPlaceholder})`, `Pickup Shop ID (e.g. ${dpdPlaceholder})`, `Pickup Shop-ID (bijv. ${dpdPlaceholder})`)}
                            style={{ ...inputStyle, flex: '1 1 160px' }}
                          />
                          <button
                            type="button"
                            data-testid="setup-dpd-request-code"
                            disabled={dpdBusy || !dpdPudoId.trim()}
                            onClick={handleDpdRequestCode}
                            style={{
                              padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                              background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 12.5,
                              opacity: (dpdBusy || !dpdPudoId.trim()) ? 0.5 : 1,
                            }}
                          >
                            📧 {tr('E-Mail-Code anfordern', 'Request email code', 'E-mailcode aanvragen')}
                          </button>
                        </div>
                        {(dpdCodeSent || dpdOtpCode) && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              data-testid="setup-dpd-otp"
                              value={dpdOtpCode}
                              onChange={(e) => setDpdOtpCode(e.target.value.toUpperCase())}
                              placeholder={tr('6-stelliger Code aus der E-Mail', '6-digit code from email', '6-cijferige code uit de e-mail')}
                              style={{ ...inputStyle, flex: '1 1 160px', letterSpacing: 1.5, fontWeight: 700 }}
                            />
                            <button
                              type="button"
                              data-testid="setup-dpd-verify-code"
                              disabled={dpdBusy || !dpdOtpCode.trim()}
                              onClick={handleDpdVerifyCode}
                              style={{
                                padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                background: '#16a34a', color: '#fff', fontWeight: 800, fontSize: 13,
                                opacity: (dpdBusy || !dpdOtpCode.trim()) ? 0.5 : 1,
                              }}
                            >
                              ✅ {tr('Verbinden', 'Connect', 'Verbinden')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {dpdMsg && (
                      <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: dpdMsg.ok ? '#16a34a' : '#dc2626' }}>
                        {dpdMsg.text}
                      </div>
                    )}

                    <div style={{ marginTop: 8 }}>
                      <Schalter
                        id="dpd-enabled"
                        an={settings.kiosk_dpd_enabled === '1'}
                        onChange={(v) => {
                          setSettings((s) => ({
                            ...s,
                            kiosk_dpd_enabled: v ? '1' : '0',
                            ...(v ? {} : { kiosk_dpd_live_annahme: '0', kiosk_dpd_live_ausgabe: '0' }),
                          }));
                        }}
                        titel={tr('📦 DPD-Übersicht, Vorab-Namen & Rückgabe-Liste aktivieren', '📦 Enable DPD overview, pre-loaded names & return list', '📦 DPD-overzicht, vooraf geladen namen & retourlijst inschakelen')}
                        text={tr('Erkennt beim Scannen sofort den Empfängernamen aus deinem DPD-Bestand (0 Sek. Wartezeit).', 'Instantly matches recipient names from your DPD inventory when scanning.', 'Herkent bij het scannen direct de naam van de ontvanger uit je DPD-voorraad.')}
                      />
                      {settings.kiosk_dpd_enabled === '1' && (
                        <>
                          <Schalter
                            id="dpd-live-annahme"
                            an={settings.kiosk_dpd_live_annahme === '1'}
                            onChange={(v) => {
                              if (v && !confirmLiveBooking('DPD Pickup')) return;
                              set('kiosk_dpd_live_annahme', v ? '1' : '0');
                            }}
                            titel={tr('⚡ Annahme live in DPD Pickup buchen', '⚡ Book parcel intake live in DPD Pickup', '⚡ Inname live in DPD Pickup boeken')}
                            text={tr('Bucht gescannte DPD-Pakete automatisch als im Shop angekommen (DELIVERY) bei DPD Pickup ein.', 'Automatically books scanned DPD parcels as received (DELIVERY) in DPD Pickup.', 'Boekt gescande DPD-pakketten automatisch als ontvangen (DELIVERY) in bij DPD Pickup.')}
                          />
                          <Schalter
                            id="dpd-live-ausgabe"
                            an={settings.kiosk_dpd_live_ausgabe === '1'}
                            onChange={(v) => {
                              if (v && !confirmLiveBooking('DPD Pickup')) return;
                              set('kiosk_dpd_live_ausgabe', v ? '1' : '0');
                            }}
                            titel={tr('⚡ Ausgabe live in DPD Pickup buchen', '⚡ Book customer pickup live in DPD Pickup', '⚡ Uitgifte live in DPD Pickup boeken')}
                            text={tr('Meldet ausgegebene DPD-Pakete direkt als abgeholt (PICKUP) an DPD Pickup.', 'Reports handed-out DPD parcels directly as picked up (PICKUP) to DPD Pickup.', 'Meldt uitgegeven DPD-pakketten direct als afgehaald (PICKUP) bij DPD Pickup.')}
                          />
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* 🟢 Vinted Go Konto-Zugang & Einstellungen (nur in verfügbaren Ländern wie NL / BE / FR) */}
                {carriers.includes('vinted') && (
                  <div
                    data-testid="setup-vinted-account-box"
                    style={{
                      marginTop: 14, padding: 12, borderRadius: 10,
                      border: `1.5px solid ${vintedAuth?.angemeldet ? '#22c55e' : '#0d9488'}`,
                      background: dark ? '#1b1f27' : '#f0fdfa',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 14 }}>
                        🟢 {tr('Vinted Go – Einstellungen & Shop-Zugang', 'Vinted Go – Settings & Shop Login', 'Vinted Go – Instellingen & shop-account')}
                      </strong>
                      {showVintedLoginBox && (
                        <span style={{
                          fontSize: 11.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                          background: vintedAuth?.angemeldet ? (dark ? '#14532d' : '#dcfce7') : (dark ? '#422006' : '#fef3c7'),
                          color: vintedAuth?.angemeldet ? (dark ? '#86efac' : '#166534') : (dark ? '#fde047' : '#92400e'),
                        }}>
                          {vintedAuth?.angemeldet
                            ? `🟢 ${tr('Verbunden', 'Connected', 'Verbonden')}`
                            : vintedAuth?.offline
                              ? `📡 ${tr('Offline', 'Offline', 'Offline')}`
                              : `🟡 ${tr('Noch nicht verbunden', 'Not connected yet', 'Nog niet verbonden')}`}
                        </span>
                      )}
                    </div>

                    {showVintedLoginBox && (
                      <>
                        <div style={{ fontSize: 12, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>
                          🔗 {tr(
                            'Unabhängige Komfort-Anbindung über deinen eigenen Vinted-Go-Shops-Zugang. Das Passwort wird nur einmalig zur Anmeldung übertragen und nicht gespeichert; alle Geräte deines Kontos teilen sich die Verbindung automatisch.',
                            'Independent convenience link using your own Vinted Go Shops login. Your password is only used once to sign in and is never stored; all devices on your account share the connection automatically.',
                            'Onafhankelijke koppeling via je eigen Vinted Go Shops-account. Je wachtwoord wordt alleen eenmalig gebruikt om in te loggen en niet opgeslagen; alle apparaten van je account delen de verbinding automatisch.'
                          )}
                        </div>
                        {vintedAuth?.angemeldet ? (
                          <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: dark ? '#14171d' : '#fff', border: `1px solid ${farbe.rand}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                            <span>
                              ✅ <strong>Vinted Go</strong>{vintedAuth.emailHinweis ? ` · ${vintedAuth.emailHinweis}` : ''}
                            </span>
                            <button
                              type="button"
                              data-testid="setup-vinted-disconnect"
                              disabled={vintedBusy}
                              onClick={handleVintedDisconnect}
                              style={{ padding: '4px 9px', borderRadius: 6, border: `1px solid ${farbe.rand}`, background: 'transparent', color: farbe.sub, fontSize: 11.5, cursor: 'pointer' }}
                            >
                              {tr('Trennen', 'Disconnect', 'Ontkoppelen')}
                            </button>
                          </div>
                        ) : (
                          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <input
                              type="email"
                              data-testid="setup-vinted-email"
                              value={vintedEmail}
                              onChange={(e) => setVintedEmail(e.target.value)}
                              placeholder={tr('E-Mail (Vinted Go Shops)', 'Email (Vinted Go Shops)', 'E-mail (Vinted Go Shops)')}
                              style={inputStyle}
                            />
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <input
                                type="password"
                                data-testid="setup-vinted-password"
                                value={vintedPasswort}
                                onChange={(e) => setVintedPasswort(e.target.value)}
                                placeholder={tr('Passwort', 'Password', 'Wachtwoord')}
                                style={{ ...inputStyle, flex: '1 1 160px' }}
                              />
                              <button
                                type="button"
                                data-testid="setup-vinted-login-btn"
                                disabled={vintedBusy || !vintedEmail.trim() || !vintedPasswort}
                                onClick={handleVintedLogin}
                                style={{
                                  padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                  background: '#0d9488', color: '#fff', fontWeight: 800, fontSize: 13,
                                  opacity: (vintedBusy || !vintedEmail.trim() || !vintedPasswort) ? 0.5 : 1,
                                }}
                              >
                                {vintedBusy ? '⏳ …' : `✅ ${tr('Verbinden', 'Connect', 'Verbinden')}`}
                              </button>
                            </div>
                          </div>
                        )}
                        {vintedMsg && (
                          <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: vintedMsg.ok ? '#16a34a' : '#dc2626' }}>
                            {vintedMsg.text}
                          </div>
                        )}
                        <Schalter
                          id="vinted-bridge-on"
                          an={settings.kiosk_vinted_bridge_on === '1'}
                          onChange={(v) => {
                            setSettings((s) => ({
                              ...s,
                              kiosk_vinted_bridge_on: v ? '1' : '0',
                              ...(v ? {} : { kiosk_vinted_live_annahme: '0', kiosk_vinted_live_ausgabe: '0' }),
                            }));
                          }}
                          titel={tr('📦 Vinted-Übersicht, Vorab-Namen & Rückgabe-Liste aktivieren', '📦 Enable Vinted overview, pre-loaded names & return list', '📦 Vinted-overzicht, vooraf geladen namen & retourlijst inschakelen')}
                          text={tr('Holt die Empfängernamen zu Vinted-Sendungsnummern automatisch aus deinem Vinted-Bestand.', 'Automatically fetches recipient names for Vinted tracking numbers from your Vinted inventory.', 'Haalt de namen van ontvangers bij Vinted-nummers automatisch op uit je Vinted-voorraad.')}
                        />
                        {settings.kiosk_vinted_bridge_on === '1' && (
                          <>
                            <Schalter
                              id="vinted-live-annahme"
                              an={settings.kiosk_vinted_live_annahme === '1'}
                              onChange={(v) => {
                                if (v && !confirmLiveBooking('Vinted Go')) return;
                                set('kiosk_vinted_live_annahme', v ? '1' : '0');
                              }}
                              titel={tr('⚡ Annahme live bei Vinted Go buchen', '⚡ Book parcel intake live in Vinted Go', '⚡ Inname live bij Vinted Go boeken')}
                              text={tr('Bucht gescannte Vinted-Pakete direkt bei Vinted Go als im Shop angekommen ein.', 'Books scanned Vinted parcels directly as received in Vinted Go.', 'Boekt gescande Vinted-pakketten direct bij Vinted Go als ontvangen in.')}
                            />
                            <Schalter
                              id="vinted-live-ausgabe"
                              an={settings.kiosk_vinted_live_ausgabe === '1'}
                              onChange={(v) => {
                                if (v && !confirmLiveBooking('Vinted Go')) return;
                                set('kiosk_vinted_live_ausgabe', v ? '1' : '0');
                              }}
                              titel={tr('⚡ Ausgabe live bei Vinted Go buchen (mit Abholcode)', '⚡ Book customer pickup live in Vinted Go (with pickup code)', '⚡ Uitgifte live bij Vinted Go boeken (met afhaalcode)')}
                              text={tr('Bestätigt die Übergabe mit dem 6-stelligen Kunden-Abholcode direkt bei Vinted Go.', 'Confirms customer pickup with the 6-digit pickup code directly in Vinted Go.', 'Bevestigt de overdracht met de 6-cijferige afhaalcode direct bij Vinted Go.')}
                            />
                          </>
                        )}
                      </>
                    )}

                    <Schalter id="vinted" an={!!features.vintedImport} onChange={(v) => feat('vintedImport', v)} stern
                      titel={t('setup.carriers.vinted')} text={t('setup.carriers.vintedHint')} />
                    {/* 📦 Vinted ohne Namen: nach der Kurznummer ablegen (V0–V9), Nummer groß aufs Etikett — Standard aus */}
                    <Schalter id="vinted-sort" an={carrierSortMap.vinted === 'kurznummer' || settings.kiosk_vinted_sort === '1'}
                      onChange={(v) => setCarrierSort('vinted', v ? 'kurznummer' : 'standard')}
                      titel={t('setup.carriers.vintedSort')} text={t('setup.carriers.vintedSortHint')} />
                  </div>
                )}
              </>
            );
          })()}

          {name === 'sorting' && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 8, marginBottom: 2 }}>
                📌 {tr('Standard-Sortierung (gilt für alle Paketdienste)', 'Default Sorting (applies to all carriers)', 'Standaard sortering (geldt voor alle pakketdiensten)')}
              </div>
              <Wahl id="sort-house" aktiv={sortHausnummer} onClick={() => setSortierung(false)}
                titel={t('setup.sorting.house')} text={t('setup.sorting.houseHint')} />
              <Wahl id="sort-surname" aktiv={sortNachname} onClick={() => setSortierung(true)} stern
                titel={t('setup.sorting.surname')} text={t('setup.sorting.surnameHint')} />
              <Wahl id="sort-barcode" aktiv={sortBarcodeOnly} onClick={() => setSortierung('barcode')} stern
                titel={tr('🏷️ Freie Regale / Nur Barcode-Scan (ohne feste Logik)', '🏷️ Free Shelves / Barcode Scan Only (no fixed logic)', '🏷️ Vrije stellingen / Alleen barcode-scan (zonder vaste logica)')}
                text={tr('Keine automatische Zuordnung nach Hausnummer oder A–Z. Du vergibst Regale frei (z. B. Vorne / Hinten) oder scannst einfach den Barcode am Regal (z. B. P6-C, Regal 1).', 'No automatic assignment by house number or A–Z. You assign shelves freely or simply scan the barcode on the shelf.', 'Geen automatische toewijzing op huisnummer of A–Z. Je wijst vakken vrij toe of scant gewoon de barcode op het schap.')} />
              {(sortNachname || Object.values(carrierSortMap).includes('nachname')) && (
                <SubShelfEditor value={parseSub(settings.kiosk_regal_sub)} stats={nachnamen.current} t={t} colors={colors}
                  onChange={(neu) => set('kiosk_regal_sub', formatSub(neu))} />
              )}

              {/* 🏷️ Regal-Barcodes & QR-Codes verwenden + Liste erstellen & drucken */}
              <div data-testid="setup-shelf-codes-box" style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}`, background: farbe.flaeche }}>
                <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  🏷️ {tr('Barcodes / QR-Codes für Regale & Lagerplätze', 'Barcodes / QR Codes for Shelves & Storage', 'Barcodes / QR-codes voor stellingen & vakken')}
                </strong>
                <div style={{ fontSize: 12, color: farbe.sub, lineHeight: 1.45, marginBottom: 8 }}>
                  {tr(
                    'Ideal für Lagerscanner (z. B. Zebra TC75, Ringscanner) oder Handy-Kamera: Erstelle deine Regalliste (z. B. Vorne, Hinten, A1–A10, P1-A bis P6-C) und drucke die Strichcodes / QR-Codes für die Regale direkt aus.',
                    'Ideal for warehouse scanners (Zebra TC75, ring scanner) or phone camera: Create your shelf list and print the barcodes / QR codes for your shelves.',
                    'Ideaal voor magazijnscanners of telefooncamera: Maak je stellinglijst en print de barcodes / QR-codes direct uit.'
                  )}
                </div>
                <Schalter
                  id="shelfqr-sorting"
                  an={settings.kiosk_qr_regal_scan === '1'}
                  onChange={(v) => set('kiosk_qr_regal_scan', v ? '1' : '0')}
                  titel={tr('📷 Regal-Barcodes / QR-Codes verwenden', '📷 Use Shelf Barcodes / QR Codes', '📷 Stelling-barcodes / QR-codes gebruiken')}
                  text={tr('Erlaubt das Scannen von Regal-Barcodes per Lagerscanner (im Hintergrund) oder per Handy-Kamera.', 'Allows scanning shelf barcodes via warehouse scanner or phone camera.', 'Maakt het scannen van stelling-barcodes mogelijk.')}
                />
                <button
                  type="button"
                  data-testid="setup-open-shelf-codes"
                  onClick={() => {
                    set('kiosk_qr_regal_scan', '1');
                    setShowShelfCodesModal(true);
                  }}
                  style={{ ...btn(true), width: '100%', marginTop: 10, flex: 'none' }}
                >
                  📋 {tr('Regal-Liste erstellen & Barcodes / QR-Codes drucken', 'Create Shelf List & Print Barcodes / QR Codes', 'Stellinglijst maken & barcodes / QR-codes printen')}
                </button>
              </div>

              {/* 🚚 Pro Paketdienst abweichend einstellen (Wunsch Folkert / Paketshops mit gemischter Sortierung) */}
              {(branche !== 'gepaeck' && branche !== 'click_collect') && (
                <div data-testid="setup-carrier-sort-box" style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}`, background: farbe.flaeche }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14 }}>
                      🚚 {tr('Pro Paketdienst abweichend sortieren (optional)', 'Custom Sorting per Carrier (optional)', 'Per pakketdienst afwijkend sorteren (optioneel)')}
                    </strong>
                    {carriers.length < CARRIERS.length && (
                      <button
                        type="button"
                        onClick={() => setShowAllCarriersSort((v) => !v)}
                        style={{
                          background: 'transparent', border: `1px solid ${farbe.rand}`, color: farbe.sub,
                          borderRadius: 14, padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        {showAllCarriersSort
                          ? tr('Nur gewählte Dienste', 'Selected carriers only', 'Alleen gekozen diensten')
                          : tr('Alle Dienste zeigen', 'Show all carriers', 'Alle diensten tonen')}
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>
                    {tr(
                      'Standardmäßig gilt die obige Einstellung für alle Dienste. Hier kannst du einzelne Paketdienste gezielt anders sortieren (z. B. PostNL nach Hausnummer, DHL nach Nachname, Vinted nach Kurznummer). Wird im Konto-Profil gespeichert.',
                      'By default, the setting above applies to all carriers. Here you can override specific carriers (e.g. PostNL by house number, DHL by last name, Vinted by short code). Saved to your account profile.',
                      'Standaard geldt de bovenstaande instelling voor alle diensten. Hier kun je specifieke pakketdiensten anders sorteren (bijv. PostNL op huisnummer, DHL op achternaam, Vinted op kort nummer).'
                    )}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(!showAllCarriersSort
                      ? CARRIERS.filter(([id]) => carriers.includes(id) || carrierSortMap[id])
                      : CARRIERS
                    ).map(([id, label]) => {
                      const cur = carrierSortMap[id] || 'standard';
                      const supportsKurz = id === 'vinted' || id === 'mondialrelay' || id === 'inpost';
                      const opts = [
                        ['standard', tr('Wie Standard', 'Default', 'Standaard')],
                        ['hausnummer', tr('🏠 Hausnr.', '🏠 House #', '🏠 Huisnr.')],
                        ['nachname', tr('🔤 A–Z', '🔤 A–Z', '🔤 A–Z')],
                        ...(supportsKurz ? [['kurznummer', tr('🔢 Kurznr.', '🔢 Short #', '🔢 Kort nr.')]] : []),
                      ];
                      return (
                        <div
                          key={id}
                          data-testid={`setup-carrier-sort-row-${id}`}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6,
                            padding: '7px 10px', borderRadius: 8, background: dark ? '#14171d' : '#fff',
                            border: `1px solid ${cur !== 'standard' ? '#1976d2' : farbe.rand}`,
                          }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 700, minWidth: 95 }}>
                            {label}
                          </span>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {opts.map(([mVal, mLabel]) => {
                              const active = cur === mVal;
                              return (
                                <button
                                  key={mVal}
                                  type="button"
                                  data-testid={`setup-carrier-sort-${id}-${mVal}`}
                                  aria-pressed={active}
                                  onClick={() => setCarrierSort(id, mVal)}
                                  style={{
                                    padding: '5px 9px', borderRadius: 14, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                    border: `1.5px solid ${active ? '#1976d2' : farbe.rand}`,
                                    background: active ? '#1976d2' : 'transparent',
                                    color: active ? '#fff' : farbe.text,
                                  }}
                                >
                                  {mLabel}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {name === 'printer' && (
            <>
              {/* Überspringen: wer keinen Drucker hat, wählt nichts aus — die Etikett-Einstellungen bleiben, wie sie sind */}
              <Wahl id="printer-none" aktiv={ohneDrucker} onClick={() => setOhneDrucker(true)}
                titel={t('setup.printer.none')} text={t('setup.printer.noneHint')} />
              <Wahl id="printer-cpcl" aktiv={!ohneDrucker && !zpl} onClick={() => { setOhneDrucker(false); set('kiosk_label_lang', 'cpcl'); }}
                titel={t('setup.printer.mobile')} text={t('setup.printer.mobileHint')} />
              <Wahl id="printer-zpl" aktiv={!ohneDrucker && zpl} onClick={() => { setOhneDrucker(false); set('kiosk_label_lang', 'zpl'); }}
                titel={t('setup.printer.desktop')}
                text={tr(
                  'Für Tisch-Etikettendrucker (Zebra, Honeywell, TSC, Citizen …) — Verbindung wahlweise über 🌐 WLAN / Netzwerkkabel (IP-Adresse) oder 🔵 Bluetooth.',
                  'For desktop label printers (Zebra, Honeywell, TSC, Citizen …) — connect via 🌐 Wi-Fi / LAN (IP address) or 🔵 Bluetooth.',
                  'Voor desktop-labelprinters (Zebra, Honeywell, TSC …) — verbind via 🌐 wifi / netwerkkabel (IP-adres) of 🔵 Bluetooth.'
                )} />
              {!ohneDrucker && (<>
              {getCustomFeatures().grenzpaket && (
                <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, border: '1.5px solid #10b981', background: dark ? '#0f291e' : '#ecfdf5', fontSize: 12.5, lineHeight: 1.45 }}>
                  🇨🇭 <strong>{tr('Grenzpaket-Vorlage automatisch aktiv:', 'Grenzpaket layout automatically active:', 'Grenzpaket-sjabloon automatisch actief:')}</strong>{' '}
                  {tr(
                    'Das Original-Grenzpaket-Quittungsetikett (mit Kundennummer-Barcode oben, Paket-Barcode B0…/K0… unten & Kalenderwoche) ist für dein Konto bereits automatisch voreingestellt!',
                    'The original Grenzpaket receipt label (with customer ID barcode, parcel barcode & calendar week) is automatically pre-selected for your account!',
                    'Het originele Grenzpaket-label is al automatisch voorgeselecteerd!'
                  )}
                </div>
              )}
              <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}` }}>
                <strong style={{ fontSize: 14 }}>🎨 {t('pw.designLabel')}</strong>
                <div style={{ marginTop: 10 }}>
                  <LabelDesigner value={cfgAusSettings(settings)} t={t} colors={colors} nachnameErlaubt={!!features.nachnameSortierung}
                    onChange={(neu) => setSettings((s) => ({ ...s, ...settingsAusCfg(neu) }))} />
                </div>
              </div>
              <Schalter id="autoprint" an={settings.kiosk_label_autoprint !== '0'} onChange={(v) => set('kiosk_label_autoprint', v ? '1' : '0')}
                titel={t('printer.autoPrint')} />
              <Schalter
                id="double-label"
                an={settings.kiosk_double_label === '1'}
                onChange={(v) => set('kiosk_double_label', v ? '1' : '0')}
                titel={tr('🎟️ Zweites Etikett als Kunden-Abholbon drucken', '🎟️ Print 2nd label as customer pickup receipt', '🎟️ Tweede label als afhaalbon voor klant printen')}
                text={tr('Druckt nach dem Paket-/Gepäcketikett ein zweites Etikett zum Abreißen als Abholschein für den Kunden.', 'Prints a second tear-off label as a pickup ticket for the customer.', 'Print een tweede afscheurbaar label als afhaalbewijs voor de klant.')}
              />
              {isPrinterSupported() && onOpenPrinter && (
                <button type="button" data-testid="setup-pair" onClick={onOpenPrinter} style={{ ...btn(false), width: '100%', marginTop: 12, flex: 'none' }}>
                  🖨️ {t('setup.printer.pair')}
                </button>
              )}
              <div style={{ fontSize: 12, color: farbe.sub, marginTop: 8, lineHeight: 1.45 }}>{t('setup.printer.pairHint')}</div>
              </>)}
              <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}`, background: farbe.flaeche }}>
                <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  🔌 {tr('Weitere Geräte & Lager-Scanner (WLAN / Bluetooth)', 'Additional Devices & Warehouse Scanners (Wi-Fi / Bluetooth)', 'Extra apparaten & magazijnscanners (Wifi / Bluetooth)')}
                </strong>
                <div style={{ fontSize: 12, color: farbe.sub, lineHeight: 1.45, marginBottom: 8 }}>
                  {tr(
                    'Verbinde WLAN-Scanner (z. B. Zebra TC75 fürs blinde Einlagern im Hinterlager), Bluetooth-/2.4GHz-Ringscanner oder Netzwerkdrucker.',
                    'Connect Wi-Fi scanners (e.g. Zebra TC75 for blind warehouse storage), Bluetooth/2.4GHz ring scanners, or network printers.',
                    'Verbind wifi-scanners (bijv. Zebra TC75 voor blind inboeken in het magazijn), Bluetooth-ringscanners of netwerkprinters.'
                  )}
                </div>
                <button
                  type="button"
                  data-testid="setup-open-devices"
                  onClick={() => setShowDevicesModal(true)}
                  style={{ ...btn(false), width: '100%', flex: 'none' }}
                >
                  🔌 {tr('Scanner & Geräte einrichten (Zebra TC75 / Ringscanner / IP-Suche)', 'Set Up Scanners & Devices (Zebra TC75 / Ring Scanner / IP Search)', 'Scanners & apparaten instellen')}
                </button>
              </div>
            </>
          )}

          {name === 'capture' && (
            <>
              <Schalter id="inapp" an={settings.kiosk_inapp_camera === '1'} onChange={(v) => { set('kiosk_inapp_camera', v ? '1' : '0'); if (!v) setSerie(false); }}
                titel={t('setup.capture.inapp')} text={t('setup.capture.inappHint')} />
              <Schalter id="series" an={!!features.seriesMode && settings.kiosk_series_mode === '1'} onChange={setSerie} stern
                titel={t('setup.capture.series')} text={t('setup.capture.seriesHint')} />
              <Schalter id="shelfqr" an={settings.kiosk_qr_regal_scan === '1'} onChange={(v) => set('kiosk_qr_regal_scan', v ? '1' : '0')}
                titel={t('setup.capture.shelfQr')} text={t('setup.capture.shelfQrHint')} />
              <Schalter id="sender" an={settings.kiosk_capture_sender === '1'} onChange={(v) => set('kiosk_capture_sender', v ? '1' : '0')}
                titel={t('setup.capture.sender')} text={t('setup.capture.senderHint')} />
              <Schalter id="list-carrier" an={settings.kiosk_list_carrier === '1'} onChange={(v) => set('kiosk_list_carrier', v ? '1' : '0')}
                titel={tr('🚚 Paketdienst in der Paketliste anzeigen', '🚚 Show carrier in the parcel list', '🚚 Vervoerder in de pakketlijst tonen')}
                text={tr('Zeigt bei jedem Paket in der Liste den Paketdienst (DHL, PostNL, Vinted …) neben dem Fach.', 'Shows the carrier (DHL, PostNL, Vinted …) next to the shelf slot for every parcel in the list.', 'Toont bij elk pakket in de lijst de vervoerder (DHL, PostNL, Vinted …) naast het vak.')} />
              <Schalter
                id="capture-palette"
                an={settings.kiosk_capture_palette === '1' || !!features.palettenvertrieb}
                onChange={(v) => { set('kiosk_capture_palette', v ? '1' : '0'); feat('palettenvertrieb', v); }}
                titel={tr('🪵 Paletten- / Gebinde-Auswahl beim Erfassen anzeigen', '🪵 Show Pallet / Packaging Type Selection', '🪵 Pallet- / verpakkingskeuze tonen bij registreren')}
                text={tr('Zeigt beim Erfassen Schnellwahl-Buttons für EU-Palette, Einwegpalette, Halbpalette, Gitterbox (sowie freie Eingabe) und blendet den Paletten-Typ als Badge in der Paketliste ein.', 'Shows quick-select buttons for Euro pallet, one-way pallet, half pallet, mesh box (plus custom input) and displays it as a badge in the list.', 'Toont snelkeuzeknoppen voor europallet, eenmalige pallet, halve pallet, gitterbox en toont het als badge in de lijst.')}
              />
              <Schalter
                id="auto-number"
                an={settings.kiosk_auto_number === '1'}
                onChange={(v) => set('kiosk_auto_number', v ? '1' : '0')}
                titel={tr('🔢 Automatische Laufnummer (#001, #002…) ohne Namen', '🔢 Auto-numbering (#001, #002…) when no name on item', '🔢 Automatisch volgnummer (#001, #002…) zonder naam')}
                text={tr('Ideal für Gepäck, Garderobe, Fundbüro oder Reparatur: Steht kein Name auf dem Gegenstand, vergibt die App automatisch eine fortlaufende Nummer.', 'Ideal for luggage, cloakroom or repairs: automatically assigns a sequential number when no name is recognized.', 'Ideaal voor bagage, garderobe of reparatie: kent automatisch een volgnummer toe als er geen naam op staat.')}
              />
              <Schalter
                id="id-first"
                an={settings.kiosk_id_first === '1'}
                onChange={(v) => set('kiosk_id_first', v ? '1' : '0')}
                titel={tr('🪪 „Ausweis zuerst" beim Erfassen anbieten / nutzen', '🪪 "ID Card First" mode when capturing', '🪪 "ID-kaart eerst" bij registreren')}
                text={tr('Liest den Namen beim Erfassen direkt vom Personalausweis, Reisepass oder Führerschein des Kunden ab.', 'Reads the customer name directly from their ID card, passport, or driver license.', 'Leest de naam direct van het identiteitsbewijs of rijbewijs van de klant.')}
              />
              <Schalter id="batch" an={!!features.batchBarcodes} onChange={(v) => feat('batchBarcodes', v)} stern
                titel={t('setup.capture.batch')} text={t('setup.capture.batchHint')} />
              {(features.batchBarcodes || hilfeOffen === 'batch') && (
                <div style={{ marginTop: 8 }}>
                  <BatchTutorialVideo compact />
                </div>
              )}
              <div style={{ marginTop: 10, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}`, background: farbe.flaeche }}>
                <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  🏪 {tr(
                    'Eigener Shop-Name / Ausgeschlossene Empfänger-Wörter (optional)',
                    'Own Shop Name / Ignored Recipient Keywords (optional)',
                    'Eigen winkelnaam / Uitgesloten ontvanger-woorden (optioneel)'
                  )}
                </strong>
                <div style={{ fontSize: 12, color: farbe.sub, lineHeight: 1.45, marginBottom: 8 }}>
                  {tr(
                    'Steht auf Paketshop-/Abholpunkt-Etiketten (z. B. DPD Shop2Shop) dein eigener Ladenname über dem Kundennamen (z.Hd.), trage ihn hier ein (mehrere Begriffe mit Komma trennen). Die KI ignoriert diese Wörter beim Scannen automatisch als Empfänger.',
                    'If parcel shop labels (e.g. DPD Shop2Shop) print your own store name above the customer name (c/o, Attn), enter it here (comma-separated). The AI will never use these words as the recipient name.',
                    'Staat op pakketpunt-labels je eigen winkelnaam boven de klantnaam, vul die dan hier in (kommagescheiden). De AI negeert deze woorden automatisch als ontvanger.'
                  )}
                </div>
                <input
                  type="text"
                  data-testid="setup-ignore-recipient"
                  maxLength={200}
                  value={settings.kiosk_ignore_recipient || ''}
                  onChange={(e) => set('kiosk_ignore_recipient', e.target.value.replace(/[^\x20-\x7e\u00a0-\u024f]/g, '').slice(0, 200))}
                  placeholder={tr('z. B. Top Phone Solution, Kiosk am Eck', 'e.g. Top Phone Solution, Corner Shop', 'bijv. The Green Goat, Pakketpunt')}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${farbe.rand}`, background: dark ? '#14171d' : '#fff',
                    color: farbe.text, fontSize: 14,
                  }}
                />
              </div>
            </>
          )}

          {name === 'handout' && (
            <>
              {/* 📒 Schalter: Empfänger- / Stammkunden-Liste (CSV & Kontakte) sichtbar machen (bei Paketshop Standard: AUS) */}
              <Schalter
                id="addressbook-enabled"
                an={settings.kiosk_addressbook_enabled === '1'}
                onChange={(v) => set('kiosk_addressbook_enabled', v ? '1' : '0')}
                titel={tr(
                  '📒 Empfänger- / Stammkunden-Liste (CSV & Kontakte) anzeigen',
                  '📒 Show Recipient / Regular Customer List (CSV & Contacts)',
                  '📒 Ontvangers- / Vaste-klantenlijst (CSV & Contacten) tonen'
                )}
                text={tr(
                  'Bei Paketshop standardmäßig aus: Schalte dies ein, wenn du eine Liste (z. B. Stammkunden mit Hinterlegungs-Notiz/Stammfach, eigene Shop-Mitarbeiter oder Büro/Wohnanlage) anlegen möchtest.',
                  'Off by default for parcel shops: Enable if you want a list of regular customers (with staff notes/default shelf), shop employees, or office/resident contacts.',
                  'Standaard uit voor pakketshops: Schakel in als je een lijst met vaste klanten (met notities/stamvak), eigen medewerkers of bewoners wilt bijhouden.'
                )}
              />

              {/* 📒 Wenn eingeschaltet: Adressbuch-Buttons + 3-Wege-Auswahl bei Listen-Treffer */}
              {settings.kiosk_addressbook_enabled === '1' && (
                <div style={{ marginTop: 10, padding: 12, borderRadius: 10, border: `1.5px solid #1976d2`, background: farbe.flaeche }}>
                  <strong style={{ fontSize: 14 }}>
                    📒 {tr('Empfänger- & Stammkunden-Liste verwalten', 'Manage Recipient & Regular Customer List', 'Ontvangers- & Vaste-klantenlijst beheren')}
                  </strong>
                  <div style={{ fontSize: 12, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>
                    {tr(
                      'Trage Stammkunden, Mitarbeiter oder Bewohner einzeln ein oder importiere sie per CSV (inkl. Stammfach & speziellem Hinweis für Mitarbeiter beim Scannen/Ausgeben).',
                      'Add regular customers, staff, or residents manually or via CSV (including default shelf & staff notes shown when scanning/handing out).',
                      'Voeg vaste klanten, medewerkers of bewoners handmatig of via CSV toe (incl. stamvak & notitie voor medewerkers).'
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    <button
                      type="button"
                      data-testid="setup-open-addressbook"
                      onClick={() => setShowAddressBookModal(true)}
                      style={{ ...btn(true), flex: 1, minWidth: 180, padding: '10px 12px', fontSize: 13 }}
                    >
                      📒 {trDyn(language, 'd07', { count: addressBookCount })}
                    </button>
                    <button
                      type="button"
                      data-testid="setup-download-csv"
                      onClick={downloadExampleCsv}
                      style={{ ...btn(false), flex: 'none', padding: '10px 12px', fontSize: 13 }}
                    >
                      📄 {tr('Beispiel-CSV', 'Example CSV', 'Voorbeeld-CSV')}
                    </button>
                  </div>

                  {/* 🎯 Was soll passieren, wenn ein Paket für jemanden auf der Liste ankommt? */}
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${farbe.rand}` }}>
                    <strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                      🔔 {tr(
                        'Was soll passieren, wenn ein Paket für jemanden auf der Liste gescannt wird?',
                        'What should happen when a parcel matches someone on your list?',
                        'Wat moet er gebeuren als een pakket overeenkomt met iemand op de lijst?'
                      )}
                    </strong>
                    <Wahl
                      id="list-notify-staff"
                      aktiv={(settings.kiosk_list_notify_mode || 'staff') === 'staff'}
                      onClick={() => setSettings((s) => ({ ...s, kiosk_list_notify_mode: 'staff', kiosk_auto_notify: '0' }))}
                      titel={tr(
                        '👷 (a) Nur Mitarbeiter am Bildschirm informieren (ohne E-Mail)',
                        '👷 (a) Only alert staff on screen (no email)',
                        '👷 (a) Alleen medewerker op het scherm informeren (geen e-mail)'
                      )}
                      text={tr(
                        'Ideal für Stammkunden & eigene Mitarbeiter im Paketshop: Zeigt beim Scannen & Ausgeben sofort einen auffälligen Hinweis am Gerät (z. B. „Auf Liste!", Hinterlegungs-Notiz, Stammfach).',
                        'Ideal for regular customers & shop staff: Immediately shows a clear banner on device when scanning & handing out (e.g. staff note, default shelf).',
                        'Ideaal voor vaste klanten & eigen medewerkers: Toont direct een duidelijke melding op het scherm met notitie en stamvak.'
                      )}
                    />
                    <Wahl
                      id="list-notify-ask"
                      aktiv={settings.kiosk_list_notify_mode === 'ask'}
                      onClick={() => setSettings((s) => ({ ...s, kiosk_list_notify_mode: 'ask', kiosk_auto_notify: '0' }))}
                      titel={tr(
                        '💬 (b) Mitarbeiter informieren & fragen, ob E-Mail rausgehen soll',
                        '💬 (b) Alert staff & ask whether to send email',
                        '💬 (b) Medewerker informeren & vragen of e-mail verstuurd moet worden'
                      )}
                      text={tr(
                        'Zeigt den Hinweis beim Scannen und öffnet nach dem Speichern ein Fenster, in dem der Mitarbeiter per Tipp entscheiden kann, ob der Kunde per E-Mail benachrichtigt wird.',
                        'Shows the staff hint when scanning and asks after saving whether to send a pickup email.',
                        'Toont de melding bij het scannen en vraagt na het opslaan of er een e-mail gestuurd moet worden.'
                      )}
                    />
                    <Wahl
                      id="list-notify-auto"
                      aktiv={settings.kiosk_list_notify_mode === 'auto'}
                      onClick={() => setSettings((s) => ({ ...s, kiosk_list_notify_mode: 'auto', kiosk_auto_notify: '1' }))}
                      titel={tr(
                        '📧 (c) Mitarbeiter informieren + vollautomatisch E-Mail senden',
                        '📧 (c) Alert staff + automatically send email',
                        '📧 (c) Medewerker informeren + automatisch e-mail sturen'
                      )}
                      text={tr(
                        'Zeigt dem Mitarbeiter Notiz & Stammfach und sendet beim Speichern sofort automatisch eine Abhol-E-Mail an die hinterlegte E-Mail-Adresse.',
                        'Shows staff note & shelf and automatically sends a pickup email on save if an email address is stored.',
                        'Toont notitie & stamvak en stuurt bij opslaan direct automatisch een afhaalmail.'
                      )}
                    />
                  </div>
                </div>
              )}

              {/* Falls die CSV-Liste aus ist, trotzdem den Schalter für die 🔔 Einzel-Warteliste (QR-Code) anbieten */}
              {settings.kiosk_addressbook_enabled !== '1' && (
                <Schalter
                  id="auto-notify"
                  an={settings.kiosk_auto_notify === '1'}
                  onChange={(v) => set('kiosk_auto_notify', v ? '1' : '0')}
                  titel={tr('📧 Automatische E-Mail bei Wartelisten-Treffer (🔔 QR-Code)', '📧 Automatic email on waiting list match (🔔 QR code)', '📧 Automatische e-mail bij wachtlijst-match (🔔 QR-code)')}
                  text={tr('Wenn sich ein Kunde über den 🔔 Benachrichtigungs-Button (QR-Code) eingetragen hat, sendet die App beim Scannen seines Pakets sofort automatisch die Abhol-E-Mail (sonst wird vorher gefragt).', 'Automatically sends a pickup email when a scanned parcel matches a customer waiting list entry.', 'Stuurt direct automatisch een afhaalmail wanneer een pakket overeenkomt met de wachtlijst.')}
                />
              )}

              <Schalter
                id="require-signature"
                an={settings.kiosk_require_signature === '1'}
                onChange={(v) => set('kiosk_require_signature', v ? '1' : '0')}
                titel={tr('✍️ Unterschrift bei Ausgabe verlangen', '✍️ Require signature on pickup', '✍️ Handtekening bij afhalen vragen')}
                text={tr('Beim Ausgeben unterschreibt der Empfänger mit Finger oder Stift direkt auf dem Bildschirm.', 'Recipient signs with finger or stylus on screen when picking up a package.', 'De ontvanger tekent bij het afhalen met vinger of pen op het scherm.')}
              />

              {isWiesnAccount() && (
                <>
                  <Schalter
                    id="bevollmaechtigter"
                    an={settings.kiosk_bevollmaechtigter === '1'}
                    onChange={(v) => {
                      set('kiosk_bevollmaechtigter', v ? '1' : '0');
                      setBevollmaechtigterEnabled(v);
                    }}
                    titel={tr(
                      '👥 Ausgabe an Bevollmächtigten ermöglichen (Nachname, optional Vorname & Vollmacht-Foto)',
                      '👥 Enable handout to authorized representative (last name, optional first name & authorization photo)',
                      '👥 Uitgifte aan gemachtigde inschakelen (achternaam, optioneel voornaam & volmacht-foto)'
                    )}
                    text={tr(
                      'Standardmäßig aus (nur für wiesn2022@). Wenn aktiv: Wisch nach rechts bleibt normale Ausgabe, zeigt aber 5 Sek. lang einen Button „👥 An Bevollmächtigten" (plus geteilten „Bevollm."-Knopf in der Paketliste). Erfasst Nachname (Vorname optional), Foto der Vollmacht sowie 1 Unterschrift für alle ausgewählten Sendungen und markiert das Paket gelb.',
                      'Off by default. Extends swipe-right overlay to 5s with an "Authorized Representative" button and splits the handout button. Captures representative last name (first name optional), photo of power of attorney, and 1 signature for multiple parcels.',
                      'Standaard uit. Verlengt swipe-naar-rechts naar 5s met een knop "Gemachtigde" en splitst de uitgifteknop.'
                    )}
                  />
                  <Schalter
                    id="dhl-stick-on"
                    an={settings.kiosk_dhl_stick_on === '1'}
                    onChange={(v) => {
                      set('kiosk_dhl_stick_on', v ? '1' : '0');
                      setDhlStickEnabled(v);
                    }}
                    titel="🔌 DHL USB-Stick Steuerung aktivieren (Schnelleinlagerung & Ausgabe am Scanner)"
                    text="Steuert euren physischen DHL-Scanner per WLAN-Stick (ESP32-S3) für Foto-Schnelleinlagerung, Multi-Paket-Ausgabe mit 1 Unterschrift und 2-Geräte-Warteschlange."
                  />
                  {settings.kiosk_dhl_stick_on === '1' && (
                    <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: `1.5px solid #ffcc00`, background: farbe.flaeche }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                        📡 Stick-IP im Laden-WLAN (z. B. 192.168.137.51 oder 192.168.178.xx):
                      </label>
                      <input
                        type="text"
                        value={settings.kiosk_dhl_stick_ip || getDhlStickIp()}
                        onChange={(e) => {
                          const ip = e.target.value;
                          set('kiosk_dhl_stick_ip', ip);
                          setDhlStickIp(ip);
                        }}
                        placeholder="192.168.137.51"
                        style={{
                          width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6,
                          border: `1px solid ${farbe.rand}`, background: dark ? '#14171d' : '#fff',
                          color: farbe.text, fontSize: 14,
                        }}
                      />
                    </div>
                  )}
                </>
              )}

              <Schalter
                id="tablet-mode"
                an={settings.kiosk_tablet_mode === '1'}
                onChange={(v) => set('kiosk_tablet_mode', v ? '1' : '0')}
                titel={tr('📱 Tablet-Suchmodus (Kunden-Suchterminal)', '📱 Tablet Search Mode (Customer Terminal)', '📱 Tablet-zoekmodus (Klantenterminal)')}
                text={tr('Blendet den Tablet-Suchmodus in der Hauptansicht ein, damit Abholer ihren Namen am Tablet eingeben und sich für E-Mail-Benachrichtigungen eintragen können.', 'Enables the tablet search filter and customer search terminal for self-service lookup and email notification signup.', 'Schakelt de tablet-zoekmodus in zodat klanten zelf hun naam kunnen zoeken of meldingen kunnen aanvragen.')}
              />

              <Schalter
                id="privacy-consent"
                an={settings.kiosk_privacy_consent === '1'}
                onChange={(v) => set('kiosk_privacy_consent', v ? '1' : '0')}
                titel={tr('🛡️ Eigene Datenschutzverordnung / Einwilligung vor Speichern', '🛡️ Custom privacy / terms consent before saving', '🛡️ Eigen privacyverklaring / toestemming vóór opslaan')}
                text={tr('Besonders wichtig bei Ausweis-Fotos: Der Kunde bestätigt vor dem Speichern deine Datenschutzerklärung auf dem Bildschirm.', 'Especially useful when photographing ID cards: customer confirms your privacy notice on screen before saving.', 'Vooral handig bij ID-foto’s: de klant bevestigt jouw privacyverklaring op het scherm vóór het opslaan.')}
              />

              {settings.kiosk_privacy_consent === '1' && (
                <div style={{ marginTop: 8, padding: 10, borderRadius: 8, border: `1px solid ${farbe.rand}`, background: farbe.flaeche }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                    📝 {tr('Dein Datenschutz-/Einwilligungstext (für den Kunden sichtbar):', 'Your privacy consent text (shown to customer):', 'Jouw toestemmingstekst (zichtbaar voor klant):')}
                  </label>
                  <textarea
                    data-testid="setup-privacy-text"
                    rows={3}
                    maxLength={600}
                    value={settings.kiosk_privacy_text || ''}
                    onChange={(e) => set('kiosk_privacy_text', e.target.value)}
                    placeholder={getPrivacyConsentText(language)}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6,
                      border: `1px solid ${farbe.rand}`, background: dark ? '#14171d' : '#fff',
                      color: farbe.text, fontSize: 12, lineHeight: 1.4, resize: 'vertical',
                    }}
                  />
                </div>
              )}

              {/* 🗑️ Speicherfrist nach Ausgabe */}
              <div style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}` }}>
                <strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  🗑️ {tr('Speicherfrist nach Ausgabe (Auto-Löschung)', 'Retention Period After Handout (Auto-Delete)', 'Bewaartermijn na afgifte (Automatisch wissen)')}
                </strong>
                <div style={{ fontSize: 12, color: farbe.sub, lineHeight: 1.4, marginBottom: 8 }}>
                  {tr(
                    'Wie lange sollen ausgegebene Pakete/Gepäckstücke (inkl. Fotos) noch im Verlauf bleiben, bevor sie endgültig gelöscht werden?',
                    'How long should handed-out items (including photos) remain in history before permanent deletion?',
                    'Hoe lang moeten uitgegeven items (incl. foto’s) bewaard blijven voordat ze definitief worden gewist?'
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                  {DELETE_DAYS_OPTIONS.map(([daysVal, labels]) => {
                    const active = (settings.kiosk_delete_days || '5') === daysVal;
                    return (
                      <button
                        key={daysVal}
                        type="button"
                        data-testid={`setup-delete-days-${daysVal}`}
                        aria-pressed={active}
                        onClick={() => set('kiosk_delete_days', daysVal)}
                        style={{
                          padding: '7px 12px', borderRadius: 18, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          border: `2px solid ${active ? '#1976d2' : farbe.rand}`,
                          background: active ? '#1976d2' : 'transparent',
                          color: active ? '#fff' : farbe.text,
                        }}
                      >
                        {(labels.key && trDyn(language, labels.key)) || labels[language] || (language === 'de' ? labels.de : labels.en)}
                      </button>
                    );
                  })}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: farbe.sub, marginLeft: 4 }}>
                    <span>{tr('Tage frei:', 'Custom days:', 'Dagen vrij:')}</span>
                    <input
                      type="number"
                      min="0"
                      max="1825"
                      data-testid="setup-delete-days-custom"
                      value={settings.kiosk_delete_days ?? ''}
                      placeholder="5"
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        if (!raw) {
                          set('kiosk_delete_days', '');
                          return;
                        }
                        const n = parseInt(raw, 10);
                        const clamped = Number.isFinite(n) ? Math.min(1825, Math.max(0, n)) : 5;
                        set('kiosk_delete_days', String(clamped));
                      }}
                      onBlur={() => {
                        if (!settings.kiosk_delete_days || !/^\d+$/.test(String(settings.kiosk_delete_days))) {
                          set('kiosk_delete_days', '5');
                        }
                      }}
                      style={{
                        width: 74, padding: '6px 8px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                        border: `1.5px solid ${farbe.rand}`, background: farbe.flaeche, color: farbe.text, textAlign: 'center',
                      }}
                    />
                    <span>{tr('(max. 1825 = 5 J.)', '(max 1825 = 5y)', '(max. 1825 = 5 j.)')}</span>
                  </label>
                </div>
                {parseInt(settings.kiosk_delete_days || '5', 10) > 5 && (
                  <div style={{ marginTop: 8, fontSize: 11.5, color: farbe.sub, lineHeight: 1.4 }}>
                    💡 {tr(
                      'Ausgegebene Pakete älter als 5 Tage wandern automatisch ins Server-Archiv (inkl. Unterschrift) und werden erst bei der Suche oder über den Archiv-Filter nachgeladen — so bleibt das Gerät immer schnell.',
                      'Handed-out packages older than 5 days automatically move to the server archive (incl. signature) and are lazy-loaded during search or via the archive filter — keeping your device fast.',
                      'Uitgegeven pakketten ouder dan 5 dagen gaan automatisch naar het serverarchief (incl. handtekening) en worden pas geladen bij het zoeken of via het archieffilter.'
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {name === 'staff' && (
            <Schalter id="staff" an={!!features.staffMode} onChange={(v) => feat('staffMode', v)} stern
              titel={t('setup.staff.enable')} text={t('setup.staff.hint')} />
          )}

          {name === 'done' && (
            <>
              <ul data-testid="setup-summary" style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}>
                <li>{tr('Einsatzbereich', 'Use case', 'Gebruiksdoel')}: {branchenName}</li>
                <li>{t('setup.carriers.title')}: {carrierNamen || '–'}
                  {carriers.includes('vinted') && settings.kiosk_vinted_sort === '1' && <> · {t('setup.carriers.vintedSort')}</>}</li>
                <li>{t('setup.sorting.title')}: {sortNachname ? t('setup.sorting.surname') : t('setup.sorting.house')}
                  {(sortNachname || Object.values(carrierSortMap).includes('nachname')) && Object.keys(parseSub(settings.kiosk_regal_sub)).length > 0 && (
                    <> · {t('setup.sub.summary')}: {Object.entries(parseSub(settings.kiosk_regal_sub)).map(([b, g]) => `${b}×${g.length + 1}`).join(', ')}</>
                  )}
                  {Object.keys(carrierSortMap).length > 0 && (
                    <> · 🚚 {Object.entries(carrierSortMap).map(([cid, m]) => {
                      const cname = (CARRIERS.find(([id]) => id === cid) || [cid, cid])[1];
                      const mLabel = m === 'hausnummer' ? tr('🏠 Hausnr.', '🏠 House #', '🏠 Huisnr.') : m === 'nachname' ? '🔤 A–Z' : tr('🔢 Kurznr.', '🔢 Short #', '🔢 Kort nr.');
                      return `${cname}: ${mLabel}`;
                    }).join(', ')}</>
                  )}</li>
                <li>{t('setup.printer.title')}: {ohneDrucker ? t('setup.printer.none') : (zpl ? t('setup.printer.desktop') : t('setup.printer.mobile'))}
                  {!ohneDrucker && settings.kiosk_double_label === '1' && <> · 🎟️ {tr('2. Kunden-Abholbon', '2nd customer receipt', '2e afhaalbon')}</>}</li>
                <li>{t('setup.capture.title')}: {[
                  settings.kiosk_inapp_camera === '1' && t('setup.capture.inapp'),
                  features.seriesMode && settings.kiosk_series_mode === '1' && t('setup.capture.series'),
                  settings.kiosk_qr_regal_scan === '1' && t('setup.capture.shelfQr'),
                  settings.kiosk_capture_sender === '1' && t('setup.capture.sender'),
                  settings.kiosk_list_carrier === '1' && tr('Paketdienst in der Liste', 'Carrier in list', 'Vervoerder in lijst'),
                  (settings.kiosk_capture_palette === '1' || features.palettenvertrieb) && tr('Paletten-Auswahl', 'Pallet selection', 'Palletkeuze'),
                  settings.kiosk_auto_number === '1' && tr('Laufnummer #001', 'Auto-number #001', 'Volgnummer #001'),
                  settings.kiosk_id_first === '1' && tr('Ausweis zuerst', 'ID first', 'ID eerst'),
                  features.batchBarcodes && t('setup.capture.batch'),
                  settings.kiosk_ignore_recipient?.trim() && `🏪 ${settings.kiosk_ignore_recipient.trim()}`,
                ].filter(Boolean).join(', ') || t('setup.done.standard')}</li>
                <li>{tr('Ausgabe & Datenschutz', 'Handout & Privacy', 'Afgifte & Privacy')}: {[
                  settings.kiosk_addressbook_enabled === '1' && trDyn(language, 'd11', { count: addressBookCount, mode: trDyn(language, { staff: 'd08', ask: 'd09', auto: 'd10' }[settings.kiosk_list_notify_mode || 'staff'] || 'd08') }),
                  settings.kiosk_addressbook_enabled !== '1' && settings.kiosk_auto_notify === '1' && tr('Auto-E-Mail (Warteliste)', 'Auto-email (waiting list)', 'Auto-e-mail (wachtlijst)'),
                  settings.kiosk_require_signature === '1' && tr('Unterschrift', 'Signature', 'Handtekening'),
                  settings.kiosk_tablet_mode === '1' && tr('Tablet-Modus', 'Tablet mode', 'Tablet-modus'),
                  settings.kiosk_privacy_consent === '1' && tr('Datenschutz-Bestätigung', 'Privacy consent', 'Privacy-toestemming'),
                  `${tr('Löschfrist', 'Retention', 'Bewaartermijn')}: ${(() => { const o = DELETE_DAYS_OPTIONS.find(([d]) => d === (settings.kiosk_delete_days || '5'))?.[1]; return (o?.key && trDyn(language, o.key)) || o?.[language === 'de' ? 'de' : language === 'nl' ? 'nl' : 'en']; })() || `${settings.kiosk_delete_days || '5'}d`}`,
                ].filter(Boolean).join(', ')}</li>
                <li>{t('setup.staff.title')}: {features.staffMode ? t('setup.done.on') : t('setup.done.off')}</li>
                {typeof onDarkMode === 'function' && <li>{t('setup.language.darkMode')}: {darkMode ? t('setup.done.on') : t('setup.done.off')}</li>}
              </ul>
              {premiumNoetig && !isPremium && (
                <div data-testid="setup-premium-note" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: farbe.flaeche, fontSize: 13, lineHeight: 1.45 }}>
                  ⭐ {t('setup.done.premiumNote')}
                </div>
              )}
              {error && <div data-testid="setup-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 10 }}>⚠️ {error}</div>}

              {/* 🔑 Profil per Code weitergeben: der Empfänger lädt eine Kopie des GESPEICHERTEN Stands */}
              <div data-testid="setup-share" style={{ marginTop: 14, padding: 12, borderRadius: 10, border: `1px solid ${farbe.rand}` }}>
                <strong style={{ fontSize: 14 }}>🔑 {t('setup.share.title')}</strong>
                <div style={{ fontSize: 13, color: farbe.sub, marginTop: 4, lineHeight: 1.45 }}>{t('setup.share.hint')}</div>
                {!start.current.hatProfil && (
                  <div data-testid="setup-share-needsave" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.45 }}>ℹ️ {t('setup.share.needSave')}</div>
                )}
                {start.current.hatProfil && !shareCode && (
                  <button type="button" data-testid="setup-share-create" disabled={shareStatus === 'busy'} onClick={teilen}
                    style={{ ...btn(false), width: '100%', marginTop: 10, flex: 'none' }}>{t('setup.share.create')}</button>
                )}
                {start.current.hatProfil && shareCode && (
                  <>
                    <div style={{ fontSize: 12, color: farbe.sub, marginTop: 10 }}>{t('setup.share.code')}</div>
                    <div data-testid="setup-share-code" style={{ fontSize: 26, fontWeight: 800, letterSpacing: 2, fontFamily: 'monospace', userSelect: 'all' }}>{codeAnzeige(shareCode)}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button type="button" data-testid="setup-share-copy" onClick={codeKopieren} style={{ ...btn(false), padding: '9px' }}>
                        {shareStatus === 'kopiert' ? `✓ ${t('setup.share.copied')}` : t('setup.share.copy')}
                      </button>
                      <button type="button" data-testid="setup-share-stop" disabled={shareStatus === 'busy'} onClick={teilenBeenden}
                        style={{ ...btn(false), padding: '9px', color: '#e5484d' }}>{t('setup.share.stop')}</button>
                    </div>
                  </>
                )}
                {shareStatus === 'fehler' && <div data-testid="setup-share-error" style={{ color: '#e5484d', fontSize: 13, marginTop: 8 }}>⚠️ {t('setup.share.failed')}</div>}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '12px 18px 16px', borderTop: `1px solid ${farbe.rand}`, display: 'flex', gap: 10 }}>
          {step > 0 && (
            <button type="button" data-testid="setup-back" disabled={busy} style={btn(false)} onClick={() => setStep(step - 1)}>{t('setup.back')}</button>
          )}
          {name === 'done' ? (
            <button type="button" data-testid="setup-save" disabled={busy} style={btn(true)} onClick={speichern}>{t('setup.save')}</button>
          ) : (
            <button type="button" data-testid="setup-next" style={btn(true)} onClick={() => setStep(step + 1)}>{t('setup.next')}</button>
          )}
        </div>
      </div>

      {showAddressBookModal && (
        <AddressBookModal
          colors={colors}
          lang={language}
          onClose={() => {
            setShowAddressBookModal(false);
            setAddressBookCount(getLocalAddressBook().length);
          }}
        />
      )}
      {showDevicesModal && (
        <DevicesModal
          setupMode={true}
          onClose={() => setShowDevicesModal(false)}
          colors={colors}
          language={language}
          onOpenPrinter={onOpenPrinter}
        />
      )}
      {showShelfCodesModal && (
        <ShelfCodesModal
          onClose={() => setShowShelfCodesModal(false)}
          colors={colors}
          language={language}
        />
      )}
    </div>
  );
}
